<template>
  <div>
    <form @submit.prevent="onValidate" :data-vv-scope="dataVvScope">
      <!--
        ⚠ isReportOpen, not a flipped useModal. The report controls at the bottom of this template sit
        OUTSIDE both layout branches, so an open CARFAX/AutoCheck report has to hide the selector from
        here. This used to assign this.useModal = false - a PROP - which Vue warns about, which the
        host's next re-render reverts, and which rendered the INLINE branch rather than hiding anything.
      -->
      <!--⚠ hideTrigger, not CSS in the host. A host that launches this selector programmatically -
           the CARFAX correction modal does - still renders this trigger and its label behind its own
           modal, and hiding them from outside would have to reach past the scoped style into a
           selector that also draws labels INSIDE the modal below. -->
      <div v-if="useModal && !isReportOpen && !hideTrigger">
        <label class="label">
          {{ label }}
          <b-icon pack="fas" icon="star" class="fa-ss"></b-icon>
        </label>
        <a class="button is-info selected-make-model" @click="isConfiguring = true">{{overview}}</a>
        <span class="is-size-7 is-inline-block" v-html="colorOverview"></span>
      </div>
      <!--⚠ THIS is the element the v-else-if below chains to, not the trigger div above it - v-else
          binds to the immediately preceding ELEMENT sibling. Both conditions must carry !isReportOpen
          or the modal stays open behind the report and the else-if is never reached.-->
      <b-modal :active.sync="isConfiguring" :full-screen="isMobile" scroll="keep" :has-modal-card="true" id="makeModelSelector" v-if="useModal && !isReportOpen" :canCancel="canCancelDialog">
        <div class="modal-card">
          <header class="modal-card-head">
              <p class="modal-card-title">{{modalTitle}}</p>
              <div class="is-pulled-right" style="margin-right:15px;" v-if="isLocated">
                <i class="icon fal fa-lg status-icon fa-exclamation-triangle has-text-danger" :title="'Vehicle will be automatically added to stock'"/>
                <label class="label is-inline-block is-size-7 has-text-danger">Vehicle will be added to stock</label>
              </div>
              <div class="columns">
                <div class="column">
                  <car-fax-control
                    :carFaxRating="selectedMakeModel.lookupResult ? selectedMakeModel.lookupResult.carFaxRating : null"  :vinNo="selectedVinNo"
                    :modalContainer="true" v-on:on-show-carfax-report="onShowCarFaxReport"
                    :resolveActionDisabled="true"
                    :hideIconWhenNoRating="true">
                  </car-fax-control>
                </div>
                <div class="column" v-if="autoCheckEnabled">
                  <auto-check-control
                    :autoCheckRating="selectedMakeModel && selectedMakeModel.lookupResult ? selectedMakeModel.lookupResult.autoCheckVehicleRating : null" :vinNo="selectedVinNo"
                    :modalContainer="true"
                    v-on:on-show-autocheck-report="onShowAutoCheckReport"
                    :purchasedFromType="purchasedFromType"
                    :inventoryType="inventoryType"
                    :inventoryTypeRequired="newInventory">
                  </auto-check-control>
                </div>
                <div class="column"></div>
              </div>
          </header>
          <!-- ⚠ Inline, not the scoped .modal-card-body rule below: the non-modal inline panel further
               down carries that same class, so a height there would stretch it too. The scoped
               max-height: calc(100vh - 220px) still applies and clamps this on short viewports. -->
          <!-- ⚠ v-show, NOT v-if. The comparison takes this modal over rather than opening a second
               one, and v-if would destroy every field in here - losing whatever the operator had
               typed and remounting the multiselects - only to rebuild it a click later. -->
          <!-- ⚠ The decode's model-group prompt REPLACES the body too, for the same reason the CARFAX
               comparison does - a prompt rendered over live fields lets the operator answer it twice,
               once in the prompt and once in the model dropdown behind it. -->
          <decoded-model-choice-panel
            v-if="showDecodedModelChoice"
            :selectedMakeModel="selectedMakeModel"
            :decodedMakeModel="decodedModelChoiceMakeModel"
            :selectedMake="decodedModelChoice && decodedModelChoice.make"
            :message="decodedModelChoiceMessage"
            :canApply="decodedModelChoiceApplyable"
            v-on:choice-changed="onDecodedModelChoiceChanged"
            v-on:apply="onDecodedModelChoiceApply"
            v-on:cancel="onDecodedModelChoiceCancel">
          </decoded-model-choice-panel>
          <decoded-model-choice-panel
            v-if="showTypedModelChoice"
            testId="typed-model-choice"
            sourceLabel="Typed"
            :selectedMakeModel="selectedMakeModel"
            :decodedMakeModel="typedModelChoiceMakeModel"
            :selectedMake="selectedMake"
            :candidates="typedModelCandidates"
            :message="typedModelChoiceMessage"
            :canApply="typedModelChoiceApplyable"
            v-on:choice-changed="onTypedModelChoiceChanged"
            v-on:apply="onTypedModelChoiceApply"
            v-on:cancel="onTypedModelChoiceCancel">
          </decoded-model-choice-panel>
          <section class="modal-card-body" style="height: 780px;" v-show="!showInlineCarfaxComparison && !showDecodedModelChoice && !showTypedModelChoice">
            <div class="box" v-if="showTypeSelector || showCostSelector || showAskingPriceSelector">
              <div class="columns">
                <div class="column is-4" v-if="showTypeSelector">
                  <button-list-selector :scope="dataVvScope" ref="locateType" validateAs="type of locate" label="Type" :required="true" v-model="selectedLocateType" :availableValues="availableLocateTypes" v-on:on-change="onSelectedLocateType" :tabindex="1"></button-list-selector>
                </div>
                <div class="column is-4" v-if="showCostSelector">
                  <div class="field">
                    <label class="label">Purchase Price<b-icon pack="fas" :icon="purchasePriceRequired ? 'star' : ''" class="fa-ss"></b-icon></label>
                    <div class="control has-icons-left">
                      <money :data-vv-scope="dataVvScope" ref="purchaseCost" name="purchaseCost" data-vv-as="purchase price" v-validate="{'required': purchasePriceRequired,'decimal': true}" data-vv-delay="250" :class="{'input': true, 'is-danger': errors.has(dataVvScope + '.purchaseCost') }" type="text" placeholder="0.00" v-model="purchaseCost" v-bind="$globalMoneyFormat" tabindex="2"></money>
                      <span v-show="errors.has(dataVvScope + '.purchaseCost')" class="help is-danger"><i v-show="errors.has(dataVvScope + '.purchaseCost')" class="fas fa-exclamation-triangle"></i> {{ errors.first(dataVvScope + '.purchaseCost') }}</span>
                      <span class="icon is-small is-left">
                        <i class="fas fa-dollar-sign"></i>
                      </span>
                    </div>
                  </div>
                </div>
                <div class="column is-4" v-if="showAskingPriceSelector">
                  <div class="field">
                    <label class="label">{{askingPriceLabel}}<b-icon pack="fas" icon="" class="fa-ss"></b-icon></label>
                    <div class="control has-icons-left">
                      <money :data-vv-scope="dataVvScope" ref="askingPrice" name="askingPrice" data-vv-as="sale price" v-validate="'decimal'" data-vv-delay="250" :class="{'input': true, 'is-danger': errors.has(dataVvScope + '.askingPrice') }" type="text" placeholder="0.00" v-model="askingPrice" v-bind="$globalMoneyFormat" tabindex="3"></money>
                      <span v-show="errors.has(dataVvScope + '.askingPrice')" class="help is-danger"><i v-show="errors.has(dataVvScope + '.askingPrice')" class="fas fa-exclamation-triangle"></i> {{ errors.first(dataVvScope + '.askingPrice') }}</span>
                      <span class="icon is-small is-left">
                        <i class="fas fa-dollar-sign"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="box" v-if="showVinSelector || showMileageSelector">
              <p v-if="vinDecodeErrors.length > 0">
                <ul class="errors">
                  <li v-for="error in vinDecodeErrors" :key="error.errorMessage">{{ error.errorMessage }}</li>
                </ul>
                <br/>
              </p>
              <div class="columns is-multiline">
                <vin-no-selector :data-vv-scope="dataVvScope" :vinRequired="forceVinCollection" v-model="selectedVinNo" v-on:vin-resolved="onVinDecodeComplete" v-on:error="onValidationError" class="column is-5" :tabIndex="4" v-if="showVinSelector"></vin-no-selector>
                <div class="column is-4" v-if="showMileageSelector">
                  <div class="field">
                    <label class="label">{{mileageTitle}}<b-icon pack="fas" :icon="usageRequired ? 'star' : ''" class="fa-ss"></b-icon></label>
                    <div class="control is-vertical-center">
                      <input name="usage" :data-vv-scope="dataVvScope"  data-vv-as="miles" v-validate="{'numeric': true, 'required': usageRequired}" :class="{'input': true, 'is-width-100': true, 'is-danger': errors.has(dataVvScope + '.usage') }" type="number" placeholder="#####" v-model="usage" :min="0" tabindex="5">
                      <usage-history-component :vinNo="selectedVinNo" :vinUsageHistory="value && value.vinUsageHistory"></usage-history-component>
                    </div>
                    <span v-show="errors.has(dataVvScope + '.usage')" class="help is-danger"><i v-show="errors.has(dataVvScope + '.usage')" class="fas fa-exclamation-triangle"></i> {{ errors.first(dataVvScope + '.usage') }}</span>
                  </div>
                </div>
                <div class="column is-3" >
                  <switch-selector type="is-danger" v-model="isDamaged" :isDisabled="isNewLocateType" tabindex="6" extraClasses="is-vertical-center">
                    <template slot="custom-label">
                      <label class="label">Damaged<b-icon pack="fal" icon="info-circle fa-sm has-text-info" :title="damageTitle"></b-icon></label>
                    </template>
                  </switch-selector>
                </div>
                <div class="column is-12" v-if="this.isDamaged">
                  <div class="field">
                    <label class="label">Description of Damage<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                    <div class="control">
                      <textarea :data-vv-scope="dataVvScope" rows="5" name="damageNote" data-vv-as="damage description" v-validate="'required'" :class="{'textarea': true, 'is-danger': errors.has(dataVvScope + '.damageNote') }" placeholder="" v-model="damageNote" tabindex="7"></textarea>
                      <span v-show="errors.has(dataVvScope + '.damageNote')" class="help is-danger"><i v-show="errors.has(dataVvScope + '.damageNote')" class="fas fa-exclamation-triangle"></i> {{ errors.first(dataVvScope + '.damageNote') }}</span>
                    </div>
                  </div>
                </div>
                <div class="column is-12" v-if="this.isDamaged">
                  <image-selector
                   :data-vv-scope="dataVvScope"
                   v-model="damageFiles"
                   label="Damage Visual Survey"
                   :required="false"
                   :isBoxed="false"
                   tabindex="8"
                    @uploadStarted="uploadStarted"
                    @uploadComplete="uploadComplete"></image-selector>
                </div>
              </div>
            </div>

            <div class="box" v-if="showSourceSelector">
              <div class="columns is-multiline">
                <div class="column is-12">
                  <button-list-selector :scope="dataVvScope" validateAs="source" label="Source" :required="true" v-model="selectedSource" :availableValues="allowedSourceTypes" :tabindex="9"></button-list-selector>
                </div>
                <div class="column is-12" v-if="selectedSource && selectedSource.id > this.definedTypes.allowedSourceTypes.Factory">
                  <div class="field is-expanded" v-if="selectedSource && selectedSource.id === this.definedTypes.allowedSourceTypes.Wholesaler">
                    <supplier-selector :data-vv-scope="dataVvScope" v-model="selectedWholeSaler" label="Wholesaler/Dealer" validateAs="purchased from wholesaler" :required="true" tabindex="10"></supplier-selector>
                  </div>
                  <div class="field is-expanded" v-if="selectedSource && selectedSource.id === this.definedTypes.allowedSourceTypes.Individual">
                    <customer-selector :data-vv-scope="dataVvScope" :selectedDealer="getCurrentDealer" v-model="selectedCustomer" label="Individual" validateAs="purchased from customer" :required="true" tabindex="11"></customer-selector>
                  </div>
                </div>
              </div>
            </div>

            <div class="box" v-if="captureTitleStatus && showTitleStatus">
              <div class="columns is-multiline">
                <div class="column is-7">
                  <button-list-selector :scope="dataVvScope" validateAs="title status" label="Title Status" :required="titleStatusRequired" v-model="selectedTitleStatus" :availableValues="allowedTitleStatusOptions" :tabindex="12"></button-list-selector>
                </div>
                <div class="column is-5">
                  <label class="label">
                    Warranty Expiration Date
                    <b-icon pack="fas" icon="" class="fa-ss"></b-icon>
                  </label>
                  <el-date-picker
                    :data-vv-scope="dataVvScope"
                    @change="onFormChange()"
                    v-model="warrantyExpirationDate"
                    type="date"
                    placeholder="Click to select..."
                    :tabindex="13"
                    popper-class="datepicker-container">
                  </el-date-picker>
                </div>
                <div class="column is-12" v-if="isTitleReceived">
                  <file-selector
                  :data-vv-scope="dataVvScope"
                  v-model="titleFiles"
                  :label="titleStatusMediaLabel"
                  :required="false"
                  :isBoxed="false"
                  tabindex="14"
                  :usePortalViewer="true"
                  @uploadStarted="uploadStarted"
                  @uploadComplete="uploadComplete">
                  </file-selector>
                </div>
              </div>
            </div>

            <div class="box" v-if="isTitlePayOff">
              <div class="columns is-multiline">
                <div class="column is-6">
                  <bank-selector :scope="dataVvScope" v-model="selectedBank" label="Title Holding Bank" validateAs="bank" :required="true"></bank-selector>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label">Account No<b-icon pack="fas" icon="star" class="fa-ss" ></b-icon></label>
                    <div class="control">
                      <input :data-vv-scope="dataVvScope" name="accNo" data-vv-as="account no" v-validate="{'required': true}" :class="{'input': true, 'is-danger': errors.has(dataVvScope + '.accNo') }" type="text" placeholder="#####" v-model="accNo" tabindex="16">
                      <span v-show="errors.has(dataVvScope + '.accNo')" class="help is-danger"><i v-show="errors.has(dataVvScope + '.accNo')" class="fas fa-exclamation-triangle"></i> {{ errors.first(dataVvScope + '.accNo') }}</span>
                    </div>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label no-content-wrap">Good Until Date<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                    <div class="control has-icons-left">
                      <el-date-picker
                        :data-vv-scope="dataVvScope"
                        @change="onFormChange()"
                        v-model="goodUntilDate"
                        type="date"
                        data-vv-as="good until date"
                        v-validate="{'required': true}"
                        data-vv-validate-on="input|close"
                        name="goodUntilDate"
                        placeholder="Click to select..."
                        :tabindex="17"
                        popper-class="datepicker-container">
                      </el-date-picker>
                      <span v-show="errors.has(dataVvScope + '.goodUntilDate')" class="help is-danger"><i v-show="errors.has(dataVvScope + '.goodUntilDate')" class="fas fa-exclamation-triangle"></i> {{ errors.first(dataVvScope + '.goodUntilDate') }}</span>
                    </div>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label no-content-wrap">Payoff Amount<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                    <div class="control has-icons-left">
                      <money :data-vv-scope="dataVvScope" name="payoffAmount" data-vv-as="payoff amount" v-validate="'decimal|required'" data-vv-delay="250" :class="{'input': true, 'is-danger': errors.has(dataVvScope + '.payoffAmount') }" type="text" placeholder="0.00" v-model="payoffAmount" v-bind="$globalMoneyFormat" tabindex="18"></money>
                      <span v-show="errors.has(dataVvScope + '.payoffAmount')" class="help is-danger"><i v-show="errors.has(dataVvScope + '.payoffAmount')" class="fas fa-exclamation-triangle"></i> {{ errors.first(dataVvScope + '.payoffAmount') }}</span>
                      <span class="icon is-small is-left">
                        <i class="fas fa-dollar-sign"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="box">
              <div class="model-error-container" v-if="modelError === true">You have selected a model that is no longer active. Please select a valid model to continue</div>
              <div class="model-error-container" v-if="yearError">{{ yearError }}</div>
              <div class="columns">
                <div class="column is-12">
                  <div class="field" v-show="!showDealerMakes">
                    <label class="label">
                      Make
                      <b-icon pack="fas" icon="star" class="fa-ss"></b-icon>
                    </label>
                    <div class="control is-fullwidth">
                      <multiselect :data-vv-scope="dataVvScope" ref="make"  :taggable="false" @tag="makeAdded" name="selectedMake" data-vv-validate-on="input|close" data-vv-as="make" v-validate="'required'" :class="{'is-danger': errors.has(dataVvScope + '.selectedMake') }" v-model="selectedMake" :options="makes" label="name" trackBy="id" @select="onSelectedMake" :tabIndex="19" :disabled="disabled" :hideSelected="true"></multiselect>
                    </div>
                    <span v-show="errors.has(dataVvScope + '.selectedMake')" class="help is-danger"><i v-show="errors.has(dataVvScope + '.selectedMake')" class="fas fa-exclamation-triangle"></i> {{ errors.first(dataVvScope + '.selectedMake') }}</span>
                  </div>
                  <button-list-selector :scope="dataVvScope" ref="makeButtons" validateAs="make" label="Make" :required="true" v-model="selectedMake" :availableValues="availableDealerMakes" v-on:on-change="onSelectedMake" v-show="showDealerMakes" :tabindex="20" :enabled="!disabled"></button-list-selector>
                </div>
              </div>

              <div class="columns">
                <div class="column is-6">
                  <div class="field" v-show="this.selectedMake">
                    <label class="label">
                      Production Year ({{vehicleAge}})
                      <b-icon pack="fas" icon="star" class="fa-ss"></b-icon>
                    </label>
                    <div class="control is-fullwidth">
                      <multiselect :data-vv-scope="dataVvScope" ref="year" :maxHeight="200"  :taggable="true" @tag="yearAdded" name="selectedYear" data-vv-validate-on="input|close" data-vv-as="year" v-validate="'required'" :class="{'is-danger': errors.has(dataVvScope + '.selectedYear') }" v-model="selectedYear" :options="productionYears" @select="onSelectedYear" :tabIndex="21" :disabled="disabled" :hideSelected="true">
                        <template slot="option" slot-scope="props"><span :class="{ 'has-text-grey': isYearOutOfRange(props.option) }">{{ yearOptionLabel(props.option) }}</span></template>
                      </multiselect>
                    </div>
                    <span v-show="errors.has(dataVvScope + '.selectedYear')" class="help is-danger is-inline-block"><i v-show="errors.has(dataVvScope + '.selectedYear')" class="fas fa-exclamation-triangle"></i> {{ errors.first(dataVvScope + '.selectedYear') }}</span>
                    <pop-fill-control v-tooltip="offeredValueTooltip('production year')" :fillableValue="decodedProductionYear" v-if="decodedProductionYear" :onFillAction="onFillProductionYear"></pop-fill-control>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field" v-show="this.selectedMake && this.selectedYear">
                    <label class="label">
                      Model
                      <b-icon pack="fas" icon="star" class="fa-ss"></b-icon>
                    </label>
                    <div class="control is-fullwidth">
                      <multiselect :data-vv-scope="dataVvScope" ref="model" :maxHeight="200" :taggable="true" @tag="modelAdded" name="selectedModel" data-vv-validate-on="input|close" data-vv-as="model" v-validate="'required'" :class="{'is-danger': errors.has(dataVvScope + '.selectedModel') }" v-model="selectedModel" :options="models" label="name" trackBy="id" @select="onSelectedModel" @close="onModelListClosed" @search-change="onModelSearchChanged" :tabIndex="22" :disabled="disabled" :hideSelected="true"><template slot="option" slot-scope="props"><span :class="modelOptionClass(props.option)">{{ modelOptionLabel(props.option) }}</span></template></multiselect>
                    </div>
                    <span v-show="errors.has(dataVvScope + '.selectedModel')" class="help is-danger is-inline-block"><i v-show="errors.has(dataVvScope + '.selectedModel')" class="fas fa-exclamation-triangle"></i> {{ errors.first(dataVvScope + '.selectedModel') }}</span>
                    <pop-fill-control v-tooltip="offeredValueTooltip('model')" :fillableValue="decodedModel.name" v-if="decodedModel" :onFillAction="onFillModel"></pop-fill-control>
                    <div class="model-not-in-catalogue" v-if="showModelNotInCatalogue">
                      <i class="fas fa-exclamation-triangle"></i>
                      Please note the vehicle model entered does not exist in our vehicle make/model data
                    </div>
                  </div>
                </div>
              </div>

              <div class="columns">
                <div class="column is-6">
                  <div class="field" v-show="this.selectedModel">
                    <label class="label">
                      Trim
                      <b-icon pack="fas" icon="" class="fa-ss"></b-icon>
                    </label>
                    <div class="control is-fullwidth">
                      <multiselect :data-vv-scope="dataVvScope" ref="trim" :maxHeight="200" :taggable="true" @tag="trimAdded" name="selectedTrim" data-vv-validate-on="input|close" data-vv-as="trim" :class="{'is-danger': errors.has(dataVvScope + '.selectedTrim') }" v-model="selectedTrim" :options="trims" @select="onSelectedTrim" :tabIndex="23" :disabled="disabled" :hideSelected="true"></multiselect>
                    </div>
                    <span v-show="errors.has(dataVvScope + '.selectedTrim')" class="help is-danger"><i v-show="errors.has(dataVvScope + '.selectedTrim')" class="fas fa-exclamation-triangle"></i> {{ errors.first(dataVvScope + '.selectedTrim') }}</span>
                    <pop-fill-control v-tooltip="offeredValueTooltip('trim')" :fillableValue="decodedTrim" v-if="decodedTrim" :onFillAction="onFillTrim"></pop-fill-control>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field" v-show="this.selectedModel">
                    <label class="label">
                      Style
                      <b-icon pack="fas" icon="" class="fa-ss"></b-icon>
                    </label>
                    <div class="control is-fullwidth">
                      <multiselect :data-vv-scope="dataVvScope" ref="style" :maxHeight="200" :taggable="true" @tag="styleAdded" name="selectedStyle" data-vv-validate-on="input|close" data-vv-as="style" :class="{'is-danger': errors.has(dataVvScope + '.selectedStyle') }" v-model="selectedStyle" :options="styles" @select="onSelectedStyle" :tabIndex="24" :disabled="disabled" :hideSelected="true"></multiselect>
                    </div>
                    <span v-show="errors.has(dataVvScope + '.selectedStyle')" class="help is-danger"><i v-show="errors.has(dataVvScope + '.selectedStyle')" class="fas fa-exclamation-triangle"></i> {{ errors.first(dataVvScope + '.selectedStyle') }}</span>
                    <pop-fill-control v-tooltip="offeredValueTooltip('style')"  :fillableValue="decodedStyle" v-if="decodedStyle" :onFillAction="onFillStyle"></pop-fill-control>
                  </div>
                </div>
              </div>

              <div class="columns">
                <div class="column is-6">
                  <div class="field" v-show="this.selectedMake && !hideColourAndTransmission">
                    <label class="label">
                      Exterior Color
                      <b-icon pack="fas" :icon="(forceColorSelection ? 'star': '')" class="fa-ss"></b-icon>
                    </label>
                    <div class="control is-fullwidth">
                      <multiselect :data-vv-scope="dataVvScope" :maxHeight="200" openDirection="top" ref="extColor" :taggable="true" @tag="onExteriorColorAdded" name="selectedExteriorColor" data-vv-validate-on="input|close" data-vv-as="exterior color" v-validate="{'required': this.forceColorSelection}" :class="{'is-danger': errors.has(dataVvScope + '.selectedExteriorColor') }" v-model="selectedExteriorColor" :options="exteriorColors" @select="onSelectedExteriorColor" :tabIndex="25" :disabled="disabled" :hideSelected="true"></multiselect>
                    </div>
                    <span v-show="errors.has(dataVvScope + '.selectedExteriorColor')" class="help is-danger"><i v-show="errors.has(dataVvScope + '.selectedExteriorColor')" class="fas fa-exclamation-triangle"></i> {{ errors.first(dataVvScope + '.selectedExteriorColor') }}</span>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field" v-show="this.selectedMake && !hideColourAndTransmission">
                    <label class="label">
                      Interior Color
                      <b-icon pack="fas" :icon="(forceColorSelection ? 'star': '')" class="fa-ss"></b-icon>
                    </label>
                    <div class="control is-fullwidth">
                      <multiselect :data-vv-scope="dataVvScope" :maxHeight="200" openDirection="top" ref="intColor" :taggable="true" @tag="onInteriorColorAdded" name="selectedInteriorColor" data-vv-validate-on="input|close" data-vv-as="interior color" v-validate="{'required': this.forceColorSelection}" :class="{'is-danger': errors.has(dataVvScope + '.selectedInteriorColor') }" v-model="selectedInteriorColor" :options="interiorColors" @select="onSelectedInteriorColor" :tabIndex="26" :disabled="disabled" :hideSelected="true"></multiselect>
                    </div>
                    <span v-show="errors.has(dataVvScope + '.selectedInteriorColor')" class="help is-danger"><i v-show="errors.has(dataVvScope + '.selectedInteriorColor')" class="fas fa-exclamation-triangle"></i> {{ errors.first(dataVvScope + '.selectedInteriorColor') }}</span>
                  </div>
                </div>
              </div>

              <div class="columns" v-show="showAlternateColors">
                <div class="column is-6">
                  <div class="field">
                    <label class="label">
                      Alternate Exterior Color
                      <b-icon pack="fas" icon="" class="fa-ss"></b-icon>
                    </label>
                    <div class="control is-fullwidth">
                      <multiselect :data-vv-scope="dataVvScope" :maxHeight="200" :hideSelected="true" multiple openDirection="top" ref="altExtColor" :taggable="true" @tag="onAltExteriorColorAdded" name="selectedAltExteriorColor" v-model="selectedAltExteriorColor" :options="altExteriorColors" :tabIndex="27" :disabled="disabled"></multiselect>
                    </div>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label">
                      Alternate Interior Color
                      <b-icon pack="fas" icon="" class="fa-ss"></b-icon>
                    </label>
                    <div class="control is-fullwidth">
                      <multiselect :data-vv-scope="dataVvScope" :maxHeight="200" :hideSelected="true" multiple openDirection="top" ref="altIntColor" :taggable="true" @tag="onAltInteriorColorAdded" name="selectedAltInteriorColor" v-model="selectedAltInteriorColor" :options="altInteriorColors" :tabIndex="28" :disabled="disabled"></multiselect>
                    </div>
                  </div>
                </div>
              </div>

              <div class="columns" v-show="this.selectedModel && !hideColourAndTransmission">
                <div class="column is-12">
                  <button-list-selector :scope="dataVvScope" validateAs="transmission type" label="Transmission" :required="forceTransmissionSelection" v-model="selectedTransmissionType" :availableValues="allowedTransmissionTypes" v-on:on-change="onSelectedTransmissionType" :enabled="!disabled"></button-list-selector>
                </div>
              </div>

              <div class="columns" v-show="showTags">
                <div class="column is-12">
                  <div class="field">
                    <label class="label">
                      Tags
                      <b-icon pack="fas" icon="" class="fa-ss"></b-icon>
                    </label>
                    <div class="control is-fullwidth">
                      <multiselect :data-vv-scope="dataVvScope" :maxHeight="200" :hideSelected="true" multiple openDirection="top" ref="tags" :taggable="true" @tag="onTagAdded" name="selectedTags" v-model="selectedTags" :options="availableTags" :tabIndex="29" :disabled="disabled"></multiselect>
                    </div>
                  </div>
                </div>
              </div>

              <div class="columns" v-show="showNotes">
                <div class="column is-12">
                  <div class="field">
                    <label class="label">
                      Notes
                      <b-icon pack="fas" icon="" class="fa-ss"></b-icon>
                    </label>
                    <div class="control is-fullwidth">
                      <textarea :data-vv-scope="dataVvScope" name="notes" :class="{'textarea': true }" rows="5" v-model="notes" @change="onNoteChanged" :tabindex="30"></textarea>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ⚠ The section-header collapses its own PARENT - baseElement is the header div's
                 parentElement unless parentSelector says otherwise - so this box is the collapse
                 target and the header must stay its first child. -->
            <div class="box" v-if="carfaxOverwriteNote">
              <section-header title="Vehicle Configuration History"></section-header>
              <div class="columns">
                <div class="column is-12">
                  <p class="heading is-inline-block" :title="carfaxOverwriteNote.note.createdAt | formatDate">{{carfaxOverwriteNote.note.createdAt | formatHumanDate}} - {{carfaxOverwriteNote.note.createdBy}}</p>
                  <p class="is-size-7">{{carfaxOverwriteNote.note.note}}</p>
                </div>
              </div>
            </div>

            <!--⚠ ON THE CONFIGURE FORM ITSELF, not only in the reconcile section further down.
                 Meeting change #14 - "nothing is showing me what Vindicode showed, I don't have a link
                 to the Carfax, nothing's helping me figure out." The reconcile section only exists
                 while a CARFAX check is mid-flight: showCarfaxComparison is host DATA the check sets,
                 so the operator who opens a saved record later to work out which model is right sees
                 none of it. Measured 28 Sep on reopened record 1083082 - both sources stored, loaded
                 and correct, and the box nowhere on screen. That is the moment the evidence is for.

                 The two copies never appear together: this body is v-show'n off whenever the
                 comparison is up, and the comparison is v-if'd away whenever it is not.

                 Same collapse contract as the box above - section-header collapses its own PARENT, so
                 the header must stay this box's first child. Collapsed by default: the operator opens
                 the selector to change the vehicle, not to read the report. -->
            <div class="box" v-if="showSourceEvidence" data-test="configure-source-evidence">
              <section-header title="What CARFAX and the VIN decode said"></section-header>
              <carfax-source-evidence
                :carfaxRating="carfaxRating"
                :vinDecodeName="vinDecodeName"
                :vinDecodeDetails="vinDecodeDetails">
              </carfax-source-evidence>
            </div>
          </section>
          <!-- ⚠ The CARFAX comparison REPLACES this modal's content rather than opening a second modal
               over it. The host raised its own b-modal before, a sibling of this one at the same
               z-index (main.scss gives every .modal.is-active 9999), so document order alone decided
               which covered which - and the CARFAX-match button in the footer below made that certain
               rather than occasional, because it cannot be clicked unless this modal is already open.
               Yes and No emit rather than acting: the host's existing handlers stay the single
               implementation, and they also own the overwrite note, the reconcile latches and the
               payment check. Both of them clear the flag, which brings the fields back. -->
          <!-- ⚠ THE MODEL HALF. The model differs, so this is a model CHOICE and uses the choice
               table. The old two-column Yes/No offered exactly two answers - take CARFAX's value or
               take nothing - and CARFAX returns the model and series as SEPARATE fields ("812" +
               "Superfast") while the catalogue row is named "812 Superfast". So Yes wrote "812" as
               free text: no catalogue link, dropped from model reporting. -->
          <section class="modal-card-body" v-if="showInlineModelChoice" data-test="inline-carfax-model-choice">
            <!-- ⚠ v-if/v-else, not a loader laid over the table. CarfaxModelReconcile settles its pre-ticked
                 row ONCE, in data(); mounted before the learned mappings arrive it never pre-ticks a
                 unanimous suggestion. So it mounts only after the fetch has settled. -->
            <div v-if="carfaxLearningPending" class="carfax-learning-loading" data-test="carfax-learning-loader">
              <b-loading :is-full-page="false" :active="true" :canCancel="false"></b-loading>
            </div>
            <carfax-model-reconcile
              v-else
              ref="inlineCarfaxModelReconcile"
              :selectedMakeModel="selectedMakeModel"
              :carfaxMakeModel="carfaxComparisonForDisplay.carfaxMakeModel"
              :selectedMake="carfaxComparisonForDisplay.carfaxMakeModel && carfaxComparisonForDisplay.carfaxMakeModel.selectedMake"
              :suggestedModel="carfaxLearnedSuggestion"
              v-on:choice-changed="inlineCarfaxChoiceApplyable = $event"
              v-on:apply="onInlineCarfaxModelApply">
            </carfax-model-reconcile>
          </section>
          <!-- ⚠ THE FIELD HALF, and it KEEPS the two-column table. The model already agrees; what
               differs is trim, style or year, so there is no model to choose and the choice table
               would have nothing to render. This is a genuine five-field diff - the one remaining
               job CarfaxVehicleComparison exists for, alongside ViewInventory's read-only copy. -->
          <section class="modal-card-body" v-if="showInlineFieldDifferences" data-test="inline-carfax-comparison">
            <div class="notification is-info is-flex">
              <i class="fas fa-info-circle is-info fa-2x"></i>
              <div class="ml-10">{{inlineCarfaxMessage}}</div>
            </div>
            <div v-if="carfaxLearningPending" class="carfax-learning-loading" data-test="carfax-learning-loader">
              <b-loading :is-full-page="false" :active="true" :canCancel="false"></b-loading>
            </div>
            <carfax-vehicle-comparison
              v-else
              :selectedMakeModel="selectedMakeModel"
              :carfaxMakeModel="carfaxComparisonForDisplay.carfaxMakeModel"
              :differences="inlineCarfaxDifferences"
              :suggestedModel="carfaxLearnedSuggestion">
            </carfax-vehicle-comparison>
            <!--⚠ The section-header collapses its own PARENT - baseElement is the header div's
                 parentElement unless parentSelector says otherwise - so this box is the collapse
                 target and the header must stay its first child. Same shape as the Vehicle
                 Configuration History box above.

                 Collapsed by default: the diff table answers the operator's question most of the
                 time, and this is the evidence they open when it does not. -->
            <div class="box" v-if="showSourceEvidence" data-test="reconcile-source-evidence">
              <section-header title="What CARFAX and the VIN decode said"></section-header>
              <carfax-source-evidence
                :carfaxRating="carfaxRating"
                :vinDecodeName="vinDecodeName"
                :vinDecodeDetails="vinDecodeDetails">
              </carfax-source-evidence>
            </div>
          </section>
          <!--⚠ TWO HALVES, now two sections and two footers rather than one of each forked inline.
               They answer different questions: the model half asks WHICH MODEL and applies the ticked
               row; the field half asks whether to take the remaining CARFAX values and applies no
               model at all, only pop-fill chips. Apply emits the chosen row - emitting nothing is what
               let the host fall through to writing CARFAX's group as free text. -->
          <!-- ⚠ KEYED, AND THE KEYS ARE LOAD-BEARING. These three footers are unkeyed siblings
               switched by v-if, and Vue 2 matches siblings by POSITION: sameVnode is true for any two of
               them - same tag, no key, both carry data - so closing one and opening the next made Vue
               REUSE the same <footer> element and patch its children in place. Child 0 of the CARFAX
               footers is Apply/Reconcile (type="button"); child 0 of this one is Done (type="submit").
               The reuse therefore rewrote the very button the browser was mid-activation on into a
               submit button: the form submitted, onValidate -> onSave cleared isConfiguring, and the
               modal closed under the operator - taking the pop-fill chips Reconcile had just armed with
               it, and clearing isDirty so a later navigation discarded the edit without prompting.
               Reproduced 21 Sep with trusted input; element.click() does NOT reproduce it, because
               synthetic events carry no activation behaviour.
               Distinct keys make sameVnode fail, so Vue destroys and creates instead of patching. -->
          <footer class="modal-card-foot" key="carfax-model-choice-footer" v-if="showInlineModelChoice">
            <button ref="applyInlineCarfax" data-test="inline-carfax-apply" class="button is-primary" type="button" :disabled="carfaxLearningPending || !inlineCarfaxChoiceApplyable" @click="$refs.inlineCarfaxModelReconcile.onApply()">Apply</button>
            <button ref="dismissInlineCarfax" data-test="inline-carfax-dismiss" class="button is-danger" type="button" @click="onDismissInlineCarfax">Cancel</button>
          </footer>
          <footer class="modal-card-foot" key="carfax-field-differences-footer" v-if="showInlineFieldDifferences">
            <button ref="reconcileInlineCarfaxFields" data-test="inline-carfax-reconcile-fields" class="button is-primary" type="button" :disabled="carfaxLearningPending" @click="onReconcileInlineCarfaxFields">Reconcile</button>
            <button data-test="inline-carfax-fields-dismiss" class="button is-danger" type="button" @click="onDismissInlineCarfax">Cancel</button>
          </footer>
          <!-- ⚠ v-if, NOT v-show, and that is forced rather than chosen. `is-block` is a Bulma display
               helper declared `display: block !important`, which beats the inline `display: none`
               v-show sets - so a v-show here renders hidden in a unit test (jsdom loads no Bulma) and
               stays on screen in the browser. Safe as v-if because this footer holds only buttons;
               the body above keeps v-show because it holds the operator's half-typed fields. -->
          <footer class="modal-card-foot is-block" key="make-model-main-footer" v-if="!showInlineCarfaxComparison && !showDecodedModelChoice && !showTypedModelChoice">
            <button :disabled="isUploadingFile" ref="done" data-test="make-model-confirm" class="button is-primary" type="submit" tabindex="31">{{ confirmLabel }}</button>
            <button ref="startOver" class="button is-danger" type="button" @click="onStartOver" tabindex="32" v-if="allowRestore === true">Start Over</button>
            <!-- ⚠ type="button" is load-bearing: this footer sits inside <form @submit.prevent>,
                 whose Done is type="submit", so a bare button would submit the form instead of
                 decoding. -->
            <!-- ⚠ `quick-action` is the circular action-button style - main.scss gives `.quick-action
                 button` a dashed 50% border - and it is a global top-level rule, so it applies from
                 here. Matches the pattern ViewInventory uses for its own footer actions rather than
                 restyling this one button. -->
            <div class="is-pulled-right quick-action" style="margin-right:15px;" v-if="canDecodeVin">
              <button ref="decodeVin" class="button" type="button" tabindex="34" :class="{'is-loading': decodingVin}" @click="onDecodeVinClick" v-tooltip="'Click to decode this VIN and offer the results as fill values. CTRL + Click will force a new run.'">
                <b-icon pack="fas" icon="barcode" custom-size="fa-lg"></b-icon>
              </button>
            </div>
            <!-- ⚠ type="button" for the same reason as the decode button above: this footer is inside
                 <form @submit.prevent> whose Done is type="submit". Compares against the report
                 already on the record - no CARFAX request, no charge.

                 ⚠ The icon is coloured so the operator can see the answer without clicking, and LEFT
                 UNCOLOURED when the comparison could not run at all - see carfaxMatchIconClass, which
                 holds the rule. A green car for a make the catalogue does not hold would say the
                 record is fine when nothing was checked. -->
            <div class="is-pulled-right quick-action" style="margin-right:15px;" v-if="canCheckCarfaxMatch">
              <button ref="checkCarfaxMatch" class="button" type="button" tabindex="35" @click="onCheckCarfaxMatchClick" v-tooltip="carfaxMatchHoverText">
                <b-icon pack="fal" icon="car-side" custom-size="fa-lg" :class="carfaxMatchIconClass"></b-icon>
              </button>
            </div>
            <div class="is-pulled-right" style="margin-right:15px;" v-if="historicalDamageDataAvailable">
              <label class="label is-inline-block is-size-7">Damage History</label>
              <i class="icon fal fa-lg status-icon" :class="getIconByLastDamageStatus()" :title="'Damage History: ' + getLastDamageDescription()"/>
            </div>
            <div class="is-pulled-right" style="margin-right:15px;" v-if="historicalAccessoryDataAvailable">
              <label class="label is-inline-block is-size-7">Accessory History</label>
              <i class="icon fal fa-lg status-icon" :class="getIconByLastAccessoryStatus()" :title="'Accessory History: ' + getLastAccessoryStatusDescription()"/>
            </div>
          </footer>
        </div>
      </b-modal>

      <!--⚠ v-else-if, not v-else: a plain v-else renders this inline form while a report is open,
          which is the behaviour the useModal flip used to produce by accident.-->
      <div v-else-if="!useModal && !isReportOpen">
          <!-- ⚠ The same prompt as the modal above, and it has to be here too. ArrivalCaptureDetail
               hosts a VIN decoder with useModal false, and the decode withholds a model group whatever
               the host renders - so without this that screen gets a blank model field and nothing to
               explain it. It sits ABOVE the fields rather than replacing them: an inline panel is part
               of a page and has no chrome of its own to take over. -->
          <decoded-model-choice-panel
            v-if="showDecodedModelChoice"
            :selectedMakeModel="selectedMakeModel"
            :decodedMakeModel="decodedModelChoiceMakeModel"
            :selectedMake="decodedModelChoice && decodedModelChoice.make"
            :message="decodedModelChoiceMessage"
            :canApply="decodedModelChoiceApplyable"
            v-on:choice-changed="onDecodedModelChoiceChanged"
            v-on:apply="onDecodedModelChoiceApply"
            v-on:cancel="onDecodedModelChoiceCancel">
          </decoded-model-choice-panel>
          <decoded-model-choice-panel
            v-if="showTypedModelChoice"
            testId="typed-model-choice"
            sourceLabel="Typed"
            :selectedMakeModel="selectedMakeModel"
            :decodedMakeModel="typedModelChoiceMakeModel"
            :selectedMake="selectedMake"
            :candidates="typedModelCandidates"
            :message="typedModelChoiceMessage"
            :canApply="typedModelChoiceApplyable"
            v-on:choice-changed="onTypedModelChoiceChanged"
            v-on:apply="onTypedModelChoiceApply"
            v-on:cancel="onTypedModelChoiceCancel">
          </decoded-model-choice-panel>
          <section class="modal-card-body make-model-inline">
            <div class="box" v-if="showTypeSelector || showCostSelector || showAskingPriceSelector">
              <div class="columns" v-if="isLocated">
                <div class="column is-12">
                  <i class="icon fal fa-lg fa-exclamation-triangle has-text-danger" :title="'Vehicle will be automatically added to stock'"/>
                  <label class="label is-inline-block is-size-7 has-text-danger">Vehicle will be added to stock</label>
                </div>
              </div>
              <div class="columns">
                <div class="column is-4" v-if="showTypeSelector">
                  <button-list-selector
                    ref="locateType" 
                    validateAs="type of locate" 
                    label="Type" 
                    :required="true" 
                    v-model="selectedLocateType" 
                    :availableValues="availableLocateTypes" 
                    v-on:on-change="onSelectedLocateType" 
                    :tabindex="1"
                    :scope="dataVvScope">
                  </button-list-selector>
                </div>
                <div class="column is-4" v-if="showCostSelector">
                  <div class="field">
                    <label class="label">Purchase Price<b-icon pack="fas" :icon="purchasePriceRequired ? 'star' : ''" class="fa-ss"></b-icon></label>
                    <div class="control has-icons-left">
                      <money ref="purchaseCost" name="purchaseCost" data-vv-as="purchase price" v-validate="{'required': purchasePriceRequired,'decimal': true}" data-vv-delay="250" :class="{'input': true, 'is-danger': errors.first(dataVvScope + '.purchaseCost') }" type="text" placeholder="0.00" v-model="purchaseCost" v-bind="$globalMoneyFormat" tabindex="2"></money>
                      <span v-show="errors.first(dataVvScope + '.purchaseCost')" class="help is-danger"><i v-show="errors.first(dataVvScope + '.purchaseCost')" class="fas fa-exclamation-triangle"></i> {{ errors.first(dataVvScope + '.purchaseCost') }}</span>
                      <span class="icon is-small is-left">
                        <i class="fas fa-dollar-sign"></i>
                      </span>
                    </div>
                  </div>
                </div>
                <div class="column is-4" v-if="showAskingPriceSelector">
                  <div class="field">
                    <label class="label">{{askingPriceLabel}}<b-icon pack="fas" icon="" class="fa-ss"></b-icon></label>
                    <div class="control has-icons-left">
                      <money ref="askingPrice" name="askingPrice" data-vv-as="sale price" v-validate="'decimal'" data-vv-delay="250" :class="{'input': true, 'is-danger': errors.first(dataVvScope + '.askingPrice') }" type="text" placeholder="0.00" v-model="askingPrice" v-bind="$globalMoneyFormat" tabindex="3"></money>
                      <span v-show="errors.first(dataVvScope + '.askingPrice')" class="help is-danger"><i v-show="errors.first(dataVvScope + '.askingPrice')" class="fas fa-exclamation-triangle"></i> {{ errors.first(dataVvScope + '.askingPrice') }}</span>
                      <span class="icon is-small is-left">
                        <i class="fas fa-dollar-sign"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="box" v-if="showVinSelector || showMileageSelector">
              <p v-if="vinDecodeErrors.length > 0">
                <ul class="errors">
                  <li v-for="error in vinDecodeErrors" :key="error.errorMessage">{{ error.errorMessage }}</li>
                </ul>
                <br/>
              </p>
              <div class="columns is-multiline">
                <vin-no-selector :vinRequired="forceVinCollection" v-model="selectedVinNo" v-on:vin-resolved="onVinDecodeComplete" v-on:error="onValidationError" class="column is-5" :tabIndex="3" v-if="showVinSelector"></vin-no-selector>
                <div class="column is-4" v-if="showMileageSelector">
                  <div class="field">
                    <label class="label">{{mileageTitle}}<b-icon pack="fas" :icon="usageRequired ? 'star' : ''" class="fa-ss"></b-icon></label>
                    <div class="control is-vertical-center">
                      <input name="usage" data-vv-as="miles" v-validate="{'numeric': true, 'required': usageRequired}" :class="{'input': true, 'is-width-100': true, 'is-danger': errors.first(dataVvScope + '.usage') }" type="number" placeholder="#####" v-model="usage" :min="0">
                      <usage-history-component :vinNo="selectedVinNo" :vinUsageHistory="value && value.vinUsageHistory"></usage-history-component>
                    </div>
                    <span v-show="errors.first(dataVvScope + '.usage')" class="help is-danger"><i v-show="errors.first(dataVvScope + '.usage')" class="fas fa-exclamation-triangle"></i> {{ errors.first(dataVvScope + '.usage') }}</span>
                  </div>
                </div>
                <div class="column is-3" >
                  <switch-selector type="is-danger" v-model="isDamaged" :isDisabled="isNewLocateType" tabindex="6" extraClasses="is-vertical-center">
                    <template slot="custom-label">
                      <label class="label">Damaged<b-icon pack="fal" icon="info-circle fa-sm has-text-info" :title="damageTitle"></b-icon></label>
                    </template>
                  </switch-selector>
                </div>
                <div class="column is-12" v-if="this.isDamaged">
                  <div class="field">
                    <label class="label">Description of Damage<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                    <div class="control">
                      <textarea rows="5" name="damageNote" data-vv-as="damage description" v-validate="'required'" :class="{'textarea': true, 'is-danger': errors.first(dataVvScope + '.damageNote') }" placeholder="" v-model="damageNote"></textarea>
                      <span v-show="errors.first(dataVvScope + '.damageNote')" class="help is-danger"><i v-show="errors.first(dataVvScope + '.damageNote')" class="fas fa-exclamation-triangle"></i> {{ errors.first(dataVvScope + '.damageNote') }}</span>
                    </div>
                  </div>
                </div>
                <div class="column is-12" v-if="this.isDamaged">
                  <image-selector
                    v-model="damageFiles"
                    label="Damage Visual Survey"
                    :required="false"
                    :isBoxed="false"
                    @uploadStarted="uploadStarted"
                    @uploadComplete="uploadComplete">
                  </image-selector>
                </div>
              </div>
            </div>

            <div class="box" v-if="showSourceSelector">
              <div class="columns is-multiline">
                <div class="column is-12">
                  <button-list-selector :scope="dataVvScope" validateAs="source" label="Source" :required="true" v-model="selectedSource" :availableValues="allowedSourceTypes"></button-list-selector>
                </div>
                <div class="column is-12" v-if="selectedSource && selectedSource.id > this.definedTypes.allowedSourceTypes.Factory">
                  <div class="field is-expanded" v-if="selectedSource && selectedSource.id === this.definedTypes.allowedSourceTypes.Wholesaler">
                    <supplier-selector :scope="dataVvScope" v-model="selectedWholeSaler" label="Wholesaler/Dealer" validateAs="purchased from wholesaler" :required="true"></supplier-selector>
                  </div>
                  <div class="field is-expanded" v-if="selectedSource && selectedSource.id === this.definedTypes.allowedSourceTypes.Individual">
                    <customer-selector :scope="dataVvScope" :selectedDealer="getCurrentDealer" v-model="selectedCustomer" label="Individual" validateAs="purchased from customer" :required="true"></customer-selector>
                  </div>
                </div>
              </div>
            </div>

            <div class="box" v-if="captureTitleStatus && showTitleStatus">
              <div class="columns is-multiline">
                <div class="column is-7">
                  <button-list-selector :scope="dataVvScope" validateAs="title status" label="Title Status" :required="titleStatusRequired" v-model="selectedTitleStatus" :availableValues="allowedTitleStatusOptions"></button-list-selector>
                </div>
                <div class="column is-5">
                  <label class="label">
                    Warranty Expiration Date
                    <b-icon pack="fas" icon="" class="fa-ss"></b-icon>
                  </label>
                  <el-date-picker
                    @change="onFormChange()"
                    v-model="warrantyExpirationDate"
                    type="date"
                    placeholder="Click to select..."
                    popper-class="datepicker-container">
                  </el-date-picker>
                </div>
                <div class="column is-12" v-if="isTitleReceived">
                  <file-selector
                  v-model="titleFiles"
                  :label="titleStatusMediaLabel"
                  :required="false"
                  :isBoxed="false"
                  :usePortalViewer="false"
                  @uploadStarted="uploadStarted"
                  @uploadComplete="uploadComplete">
                  </file-selector>
                </div>
              </div>
            </div>

            <div class="box" v-if="isTitlePayOff">
              <div class="columns is-multiline">
                <div class="column is-6">
                  <bank-selector :scope="dataVvScope" v-model="selectedBank" label="Title Holding Bank" validateAs="bank" :required="true"></bank-selector>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label">Account No<b-icon pack="fas" icon="star" class="fa-ss" ></b-icon></label>
                    <div class="control">
                      <input name="accNo" data-vv-as="account no" v-validate="{'required': true}" :class="{'input': true, 'is-danger': errors.first(dataVvScope + '.accNo') }" type="text" placeholder="#####" v-model="accNo">
                      <span v-show="errors.first(dataVvScope + '.accNo')" class="help is-danger"><i v-show="errors.first(dataVvScope + '.accNo')" class="fas fa-exclamation-triangle"></i> {{ errors.first(dataVvScope + '.accNo') }}</span>
                    </div>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label no-content-wrap">Good Until Date<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                    <div class="control has-icons-left">
                      <el-date-picker
                        @change="onFormChange()"
                        v-model="goodUntilDate"
                        type="date"
                        data-vv-as="good until date"
                        v-validate="{'required': true}"
                        data-vv-validate-on="input|close"
                        name="goodUntilDate"
                        placeholder="Click to select..."
                        popper-class="datepicker-container">
                      </el-date-picker>
                      <span v-show="errors.first(dataVvScope + '.goodUntilDate')" class="help is-danger"><i v-show="errors.first(dataVvScope + '.goodUntilDate')" class="fas fa-exclamation-triangle"></i> {{ errors.first(dataVvScope + '.goodUntilDate') }}</span>
                    </div>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label no-content-wrap">Payoff Amount<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                    <div class="control has-icons-left">
                      <money name="payoffAmount" data-vv-as="payoff amount" v-validate="'decimal|required'" data-vv-delay="250" :class="{'input': true, 'is-danger': errors.first(dataVvScope + '.payoffAmount') }" type="text" placeholder="0.00" v-model="payoffAmount" v-bind="$globalMoneyFormat"></money>
                      <span v-show="errors.first(dataVvScope + '.payoffAmount')" class="help is-danger"><i v-show="errors.first(dataVvScope + '.payoffAmount')" class="fas fa-exclamation-triangle"></i> {{ errors.first(dataVvScope + '.payoffAmount') }}</span>
                      <span class="icon is-small is-left">
                        <i class="fas fa-dollar-sign"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="box" v-if="!isModelInfoCollapsed">
              <div class="model-error-container" v-if="modelError === true">You have selected a model that is no longer active. Please select a valid model to continue</div>
              <div class="model-error-container" v-if="yearError">{{ yearError }}</div>
              <div class="columns">
                <div class="column is-12">
                  <div class="field" v-show="!showDealerMakes">
                    <label class="label">
                      Make
                      <b-icon pack="fas" icon="star" class="fa-ss"></b-icon>
                    </label>
                    <div class="control is-fullwidth">
                      <multiselect :data-vv-scope="dataVvScope" ref="make"  :taggable="false" @tag="makeAdded" name="selectedMake" data-vv-validate-on="input|close" data-vv-as="make" v-validate="'required'" :class="{'is-danger': errors.has(dataVvScope + '.selectedMake') }" v-model="selectedMake" :options="makes" label="name" trackBy="id" @select="onSelectedMake" :tabindex="2" :disabled="disabled" :hideSelected="true"></multiselect>
                    </div>
                    <span v-show="errors.has(dataVvScope + '.selectedMake')" class="help is-danger"><i v-show="errors.has(dataVvScope + '.selectedMake')" class="fas fa-exclamation-triangle"></i> {{ errors.first(dataVvScope + '.selectedMake') }}</span>
                  </div>
                  <button-list-selector v-if="showDealerMakes"
                    :scope="dataVvScope"
                    ref="makeButtons" 
                    validateAs="make"
                    label="Make"
                    :required="true"
                    v-model="selectedMake"
                    :availableValues="availableDealerMakes"
                    v-on:on-change="onSelectedMake"
                    :tabindex="2"
                    :enabled="!disabled">
                  </button-list-selector>
                </div>
              </div>

              <div class="columns">
                <div class="column is-6">
                  <div class="field" v-show="this.selectedMake">
                    <label class="label">
                      Production Year ({{vehicleAge}})
                      <b-icon pack="fas" icon="star" class="fa-ss"></b-icon>
                    </label>
                    <div class="control is-fullwidth">
                      <multiselect :data-vv-scope="dataVvScope" :maxHeight="200" ref="year"  :taggable="true" @tag="yearAdded" name="selectedYear" data-vv-validate-on="input|close" data-vv-as="year" v-validate="'required'" :class="{'is-danger': errors.has(dataVvScope + '.selectedYear') }" v-model="selectedYear" :options="productionYears" @select="onSelectedYear" :tabindex="3" :disabled="disabled" :hideSelected="true">
                        <template slot="option" slot-scope="props"><span :class="{ 'has-text-grey': isYearOutOfRange(props.option) }">{{ yearOptionLabel(props.option) }}</span></template>
                      </multiselect>
                    </div>
                    <span v-show="errors.has(dataVvScope + '.selectedYear')" class="help is-danger is-inline-block"><i v-show="errors.has(dataVvScope + '.selectedYear')" class="fas fa-exclamation-triangle"></i> {{ errors.first(dataVvScope + '.selectedYear') }}</span>
                    <pop-fill-control v-tooltip="offeredValueTooltip('production year')"  :fillableValue="decodedProductionYear" v-if="decodedProductionYear" :onFillAction="onFillProductionYear"></pop-fill-control>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field" v-show="this.selectedMake && this.selectedYear">
                    <label class="label">
                      Model
                      <b-icon pack="fas" icon="star" class="fa-ss"></b-icon>
                    </label>
                    <div class="control is-fullwidth">
                      <multiselect :data-vv-scope="dataVvScope" ref="model" :maxHeight="200" :taggable="true" @tag="modelAdded" name="selectedModel" data-vv-validate-on="input|close" data-vv-as="model" v-validate="'required'" :class="{'is-danger': errors.has(dataVvScope + '.selectedModel') }" v-model="selectedModel" :options="models" label="name" trackBy="id" @select="onSelectedModel" @close="onModelListClosed" @search-change="onModelSearchChanged" :tabindex="4" :disabled="disabled" :hideSelected="true"><template slot="option" slot-scope="props"><span :class="modelOptionClass(props.option)">{{ modelOptionLabel(props.option) }}</span></template></multiselect>
                    </div>
                    <span v-show="errors.has(dataVvScope + '.selectedModel')" class="help is-danger is-inline-block"><i v-show="errors.has(dataVvScope + '.selectedModel')" class="fas fa-exclamation-triangle"></i> {{ errors.first(dataVvScope + '.selectedModel') }}</span>
                    <pop-fill-control v-tooltip="offeredValueTooltip('model')" :fillableValue="decodedModel.name" v-if="decodedModel" :onFillAction="onFillModel"></pop-fill-control>
                    <div class="model-not-in-catalogue" v-if="showModelNotInCatalogue">
                      <i class="fas fa-exclamation-triangle"></i>
                      Please note the vehicle model entered does not exist in our vehicle make/model data
                    </div>
                  </div>
                </div>
              </div>

              <div class="columns">
                <div class="column is-6">
                  <div class="field" v-show="this.selectedModel">
                    <label class="label">
                      Trim
                      <b-icon pack="fas" icon="" class="fa-ss"></b-icon>
                    </label>
                    <div class="control is-fullwidth">
                      <multiselect :data-vv-scope="dataVvScope" ref="trim" :maxHeight="200" :taggable="true" @tag="trimAdded" name="selectedTrim" data-vv-validate-on="input|close" data-vv-as="trim" :class="{'is-danger': errors.has(dataVvScope + '.selectedTrim') }" v-model="selectedTrim" :options="trims" @select="onSelectedTrim" :tabindex="5" :disabled="disabled" :hideSelected="true"></multiselect>
                    </div>
                    <span v-show="errors.has(dataVvScope + '.selectedTrim')" class="help is-danger"><i v-show="errors.has(dataVvScope + '.selectedTrim')" class="fas fa-exclamation-triangle"></i> {{ errors.first(dataVvScope + '.selectedTrim') }}</span>
                    <pop-fill-control v-tooltip="offeredValueTooltip('trim')" :fillableValue="decodedTrim" v-if="decodedTrim" :onFillAction="onFillTrim"></pop-fill-control>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field" v-show="this.selectedModel">
                    <label class="label">
                      Style
                      <b-icon pack="fas" icon="" class="fa-ss"></b-icon>
                    </label>
                    <div class="control is-fullwidth">
                      <multiselect :data-vv-scope="dataVvScope" ref="style" :maxHeight="200" :taggable="true" @tag="styleAdded" name="selectedStyle" data-vv-validate-on="input|close" data-vv-as="style" :class="{'is-danger': errors.has(dataVvScope + '.selectedStyle') }" v-model="selectedStyle" :options="styles" @select="onSelectedStyle" :tabindex="6" :disabled="disabled" :hideSelected="true"></multiselect>
                    </div>
                    <span v-show="errors.has(dataVvScope + '.selectedStyle')" class="help is-danger"><i v-show="errors.has(dataVvScope + '.selectedStyle')" class="fas fa-exclamation-triangle"></i> {{ errors.first(dataVvScope + '.selectedStyle') }}</span>
                    <pop-fill-control v-tooltip="offeredValueTooltip('style')" :fillableValue="decodedStyle" v-if="decodedStyle" :onFillAction="onFillStyle"></pop-fill-control>
                  </div>
                </div>
              </div>

              <div class="columns">
                <div class="column is-6">
                  <div class="field" v-show="this.selectedMake && !hideColourAndTransmission">
                    <label class="label">
                      Exterior Color
                      <b-icon pack="fas" :icon="(forceColorSelection ? 'star': '')" class="fa-ss"></b-icon>
                    </label>
                    <div class="control is-fullwidth">
                      <multiselect :data-vv-scope="dataVvScope" :maxHeight="200" openDirection="top" ref="extColor" :taggable="true" @tag="onExteriorColorAdded" name="selectedExteriorColor" data-vv-validate-on="input|close" data-vv-as="exterior color" v-validate="{'required': this.forceColorSelection}" :class="{'is-danger': errors.has(dataVvScope + '.selectedExteriorColor') }" v-model="selectedExteriorColor" :options="exteriorColors" @select="onSelectedExteriorColor" :tabindex="7" :disabled="disabled" :hideSelected="true"></multiselect>
                    </div>
                    <span v-show="errors.has(dataVvScope + '.selectedExteriorColor')" class="help is-danger"><i v-show="errors.has(dataVvScope + '.selectedExteriorColor')" class="fas fa-exclamation-triangle"></i> {{ errors.first(dataVvScope + '.selectedExteriorColor') }}</span>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field" v-show="this.selectedMake && !hideColourAndTransmission">
                    <label class="label">
                      Interior Color
                      <b-icon pack="fas" :icon="(forceColorSelection ? 'star': '')" class="fa-ss"></b-icon>
                    </label>
                    <div class="control is-fullwidth">
                      <multiselect :data-vv-scope="dataVvScope" :maxHeight="200" openDirection="top" ref="intColor" :taggable="true" @tag="onInteriorColorAdded" name="selectedInteriorColor" data-vv-validate-on="input|close" data-vv-as="interior color" v-validate="{'required': this.forceColorSelection}" :class="{'is-danger': errors.has(dataVvScope + '.selectedInteriorColor') }" v-model="selectedInteriorColor" :options="interiorColors" @select="onSelectedInteriorColor" :tabindex="8" :disabled="disabled" :hideSelected="true"></multiselect>
                    </div>
                    <span v-show="errors.has(dataVvScope + '.selectedInteriorColor')" class="help is-danger"><i v-show="errors.has(dataVvScope + '.selectedInteriorColor')" class="fas fa-exclamation-triangle"></i> {{ errors.first(dataVvScope + '.selectedInteriorColor') }}</span>
                  </div>
                </div>
              </div>

              <div class="columns" v-show="showAlternateColors">
                <div class="column is-6">
                  <div class="field">
                    <label class="label">
                      Alternate Exterior Color
                      <b-icon pack="fas" icon="" class="fa-ss"></b-icon>
                    </label>
                    <div class="control is-fullwidth">
                      <multiselect :hideSelected="true" :maxHeight="200" multiple openDirection="top" ref="altExtColor" :taggable="true" @tag="onAltExteriorColorAdded" name="selectedAltExteriorColor" v-model="selectedAltExteriorColor" :options="altExteriorColors" :tabindex="9" :disabled="disabled"></multiselect>
                    </div>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label">
                      Alternate Interior Color
                      <b-icon pack="fas" icon="" class="fa-ss"></b-icon>
                    </label>
                    <div class="control is-fullwidth">
                      <multiselect :hideSelected="true" :maxHeight="200" multiple openDirection="top" ref="altIntColor" :taggable="true" @tag="onAltInteriorColorAdded" name="selectedAltInteriorColor" v-model="selectedAltInteriorColor" :options="altInteriorColors" :tabindex="10" :disabled="disabled"></multiselect>
                    </div>
                  </div>
                </div>
              </div>

              <div class="columns" v-show="this.selectedModel && !hideColourAndTransmission">
                <div class="column is-12">
                  <button-list-selector :scope="dataVvScope" validateAs="transmission type" label="Transmission" :required="false" v-model="selectedTransmissionType" :availableValues="allowedTransmissionTypes" v-on:on-change="onSelectedTransmissionType" :enabled="!disabled"></button-list-selector>
                </div>
              </div>

              <div class="columns" v-show="showTags">
                <div class="column is-12">
                  <div class="field">
                    <label class="label">
                      Tags
                      <b-icon pack="fas" icon="" class="fa-ss"></b-icon>
                    </label>
                    <div class="control is-fullwidth">
                      <multiselect :hideSelected="true" :maxHeight="200" multiple openDirection="top" ref="tags" :taggable="true" @tag="onTagAdded" name="selectedTags" v-model="selectedTags" :options="availableTags" :tabindex="11" :disabled="disabled"></multiselect>
                    </div>
                  </div>
                </div>
              </div>

              <div class="columns" v-show="showNotes">
                <div class="column is-12">
                  <div class="field">
                    <label class="label">
                      Notes
                      <b-icon pack="fas" icon="" class="fa-ss"></b-icon>
                    </label>
                    <div class="control is-fullwidth">
                      <textarea name="notes" :class="{'textarea': true }" rows="5" v-model="notes" @change="onNoteChanged" :tabindex="12"></textarea>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="box" v-if="historicalDamageDataAvailable || historicalAccessoryDataAvailable">
              <div class="is-pulled-right" style="margin-right:15px;" v-if="historicalDamageDataAvailable">
                <label class="label is-inline-block is-size-7">Damage History</label>
                <i class="icon fal fa-lg status-icon" :class="getIconByLastDamageStatus()" :title="'Damage History: ' + getLastDamageDescription()"/>
              </div>
              <div class="is-pulled-right" style="margin-right:15px;" v-if="historicalAccessoryDataAvailable">
                <label class="label is-inline-block is-size-7">Accessory History</label>
                <i class="icon fal fa-lg status-icon" :class="getIconByLastAccessoryStatus()" :title="'Accessory History: ' + getLastAccessoryStatusDescription()"/>
              </div>
            </div>

            <button ref="done" class="button is-primary" type="submit" tabindex="13" style="visibility:hidden">Done</button>

          </section>
      </div>
      <p></p>
    </form>
    <auto-check-report-control
      v-if="autoCheckEnabled"
      title="AutoCheck Vehicle Status"
      :vinNo="selectedVinNo"
      v-on:on-hide-autocheck-report="onHideAutoCheckReport"
      :showAutoCheckReportModal="showReport">
    </auto-check-report-control>
    <car-fax-report-control
      title="CarFax Vehicle Status"
      v-on:on-hide-carfax-report="onHideCarFaxReport"
      :showCarFaxReportModal="showCarFaxReport"
      :carFaxReportUrl="carFaxReportUrl">
    </car-fax-report-control>
  </div>
</template>

<script>

import eventBus from '@/eventBus'
import { createNamespacedHelpers } from 'vuex'
import moment from 'moment'
import ButtonListSelector from '@/components/generic/ButtonListSelector'
import PopFillControl from '@/components/generic/PopFillControl'
import CarFaxControl from '@/components/generic/CarFaxControl'
import ImageSelector from '@/components/generic/ImageSelector'
import FileSelector from '@/components/generic/FileSelector'
import VinNoSelector from '@/components/generic/VinNoSelector'
import CustomerSelector from '@/components/generic/CustomerSelector'
import SupplierSelector from '@/components/generic/SupplierSelector'
import SectionHeader from '@/components/generic/SectionHeader'
import UsageHistoryComponent from '@/components/inventory/UsageHistoryComponent'
import validateBeforeSubmitMixin from '@/mixins/generic/validateBeforeSubmit'
import BankSelector from '@/components/generic/BankSelector'
import SwitchSelector from '@/components/generic/SwitchSelector'
import AutoCheckControl from '@/components/generic/AutoCheckControl'
import AutoCheckReportControl from '@/components/generic/AutoCheckReportControl'
import CarFaxReportControl from '@/components/generic/CarFaxReportControl'
import { isPlausibleProductionYear, maxProductionYear, selectableModels, sortedProductionYears } from '@/utils/vehicle_make_version'
import { modelNameCarriesTrim, findCarfaxOverwriteNote, resolveCarfaxMakeModel, isMeaningfulValue, buildReconcileMessage, buildFieldDifferencesMessage, buildOfferedValueTooltip, readMakeModelFields , findLearnedMapping, isKeepCurrentModel } from '@/utils/carfax_make_model_resolution'
import { buildCarfaxComparison, carfaxMatchOutcome, carfaxModelNameDiffers, carfaxMatchIconClass, carfaxMatchHoverText, carfaxSeriesTrimOffer } from '@/utils/carfax_make_model_comparison'
import { hasCarfaxTesterRole } from '@/utils/carfax_roles'
import CarfaxVehicleComparison from '@/components/generic/CarfaxVehicleComparison'
import CarfaxSourceEvidence from '@/components/generic/CarfaxSourceEvidence'
import CarfaxModelReconcile from '@/components/generic/CarfaxModelReconcile'
import DecodedModelChoicePanel from '@/components/generic/DecodedModelChoicePanel'
import decodedModelChoiceMixin from '@/mixins/generic/decodedModelChoice'
import typedModelChoiceMixin from '@/mixins/generic/typedModelChoice'
import inventoryService from '@/services/inventoryService'
import carfaxModelMappingService from '@/services/carfaxModelMappingService'

const mapConfigGetters = createNamespacedHelpers('config').mapGetters
const mapVehicleMakeGetters = createNamespacedHelpers('vehicleMake').mapGetters
const mapDealerGetters = createNamespacedHelpers('dealer').mapGetters
const mapUserGetters = createNamespacedHelpers('user').mapGetters

export default {
  inject: {
    $validator: '$validator'
  },
  name: 'MakeModelSelector',
  mixins: [validateBeforeSubmitMixin, decodedModelChoiceMixin, typedModelChoiceMixin],
  components: {
    'button-list-selector': ButtonListSelector,
    'carfax-vehicle-comparison': CarfaxVehicleComparison,
    'carfax-source-evidence': CarfaxSourceEvidence,
    //⚠ Both tables are registered, and both are used - the model half asks which model, the field
    //half shows the five-field diff. See the two sections in the inline CARFAX block.
    'carfax-model-reconcile': CarfaxModelReconcile,
    'decoded-model-choice-panel': DecodedModelChoicePanel,
    'car-fax-control': CarFaxControl,
    'customer-selector': CustomerSelector,
    'supplier-selector': SupplierSelector,
    'vin-no-selector': VinNoSelector,
    'section-header': SectionHeader,
    'usage-history-component': UsageHistoryComponent,
    'image-selector': ImageSelector,
    'file-selector': FileSelector,
    'bank-selector': BankSelector,
    'switch-selector': SwitchSelector,
    'auto-check-control': AutoCheckControl,
    'auto-check-report-control': AutoCheckReportControl,
    'pop-fill-control': PopFillControl,
    'car-fax-report-control': CarFaxReportControl
  },
  props: {
    label: {
      type: String,
      default: 'Select/Configure Vehicle'
    },
    value: {
      type: Object,
      default: null
    },
    inventoryType: {
      type: Object,
      default: null
    },
    stockType: {
      type: Object,
      default: null
    },
    forceColorSelection: {
      type: Boolean,
      default: true
    },
    showTypeSelector: {
      type: Boolean,
      default: false
    },
    showCostSelector: {
      type: Boolean,
      default: false
    },
    showAskingPriceSelector: {
      type: Boolean,
      default: false
    },
    showMileageSelector: {
      type: Boolean,
      default: false
    },
    captureTitleStatus: {
      type: Boolean,
      default: false
    },
    showVinSelector: {
      type: Boolean,
      default: false
    },
    showSourceSelector: {
      type: Boolean,
      default: false
    },
    showAlternateColors: {
      type: Boolean,
      default: false
    },
    showTags: {
      type: Boolean,
      default: false
    },
    showNotes: {
      type: Boolean,
      default: false
    },
    allowRestore: {
      type: Boolean,
      default: false
    },
    /*
     * The confirm button's wording. 'Done' everywhere by default, so all thirteen existing hosts are
     * untouched; the quick view passes 'Save'.
     *
     * ⚠ A LABEL, not a second button. The footer immediately below documents a Vue 2 positional-reuse
     * bug that rewrote the button the browser was mid-activation on - child 0 of this footer is
     * load-bearing. Swapping text leaves the element identity alone; a v-if pair of buttons would put
     * a second element into exactly the position that bug lives in.
     *
     * ⚠ Wording only. Done and Save do the same thing - submit the form - because on the quick view
     * closing the selector IS the save (CarfaxModelCorrectionModal.onConfiguringChange posts on
     * close). 'Done' understated that: it reads like dismissing a dialog, not writing to the record.
     */
    confirmLabel: {
      type: String,
      default: 'Done'
    },
    /*
     * Hides the two colour pickers and the transmission picker, for a host that cannot save them.
     *
     * ⚠ CarfaxModelCorrectionModal posts the vehicle DESCRIPTION alone - make, model, year, trim, style
     * - and nothing else. With these showing, an operator could pick a colour, press Save, read
     * "Vehicle model updated" and lose the colour without a word. Off by default: every other host
     * saves the whole record and needs them.
     */
    hideColourAndTransmission: {
      type: Boolean,
      default: false
    },
    isLocated: {
      type: Boolean,
      default: false
    },
    forceVinCollection: {
      type: Boolean,
      default: false
    },
    useModal: {
      type: Boolean,
      default: true
    },
    dataVvScope: {
      type: String,
      default: 'mms'
    },
    canCancelDialog: {
      type: Boolean,
      default: false
    },
    /*
     * Suppresses the inline trigger button and its label, for a host that opens this selector
     * programmatically rather than letting the operator click into it. Defaults false, so every
     * existing host is untouched.
     */
    hideTrigger: {
      type: Boolean,
      default: false
    },
    forceTransmissionSelection: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    purchasedFromType: {
      type: String,
      default: ''
    },
    newInventory: {
      type: Boolean,
      default: false
    },
    collapseMakeModelInfo: {
      type: Boolean,
      default: false
    },
    useValidator: {
      type: Object,
      default: null
    },
    /*
     * The inventory record's own notes, so the restore control below can find the CARFAX overwrite
     * note and read back what that apply replaced.
     *
     * ⚠ NOT named `notes`. This component already owns a `notes` data property - the free-text search
     * notes box, which round-trips as the SearchNotes attribute - and shadowing it would break that.
     *
     * ⚠ No fetch and no backend change: InventoryModel already serialises its notes, so the record a
     * host has loaded carries them. Hosts with no inventory record simply pass nothing and the control
     * never renders.
     */
    inventoryNotes: {
      type: Array,
      default: null
    },
    /*
     * The record's VIN, so the footer action below can decode it without leaving the modal.
     *
     * ⚠ It has to be PASSED IN. This component's own selectedVinNo is '' on a plain load - there is
     * no vinNo prop today and the hosts' selectedMakeModel literals carry no vinNo key - so it only
     * ever holds a value after a decode has already happened. Hosts with no VIN pass nothing and the
     * action never renders.
     */
    /*
     * The record's own id, sent with the decode below.
     *
     * ⚠ Load-bearing, not cosmetic. VinValidator refuses a VIN that is already in stock unless
     * `existingInventory.Id == vinModel.Id` - so a decode that omits the id makes an existing record
     * collide with ITSELF and comes back "already in stock". Every EditInventory record is in stock,
     * so the action would never work there. NewInventory deliberately passes nothing: a genuinely new
     * record SHOULD be refused when the VIN is already on another vehicle.
     */
    inventoryId: {
      type: Number,
      default: null
    },
    vinNo: {
      type: String,
      default: ''
    },
    /*
     * The CARFAX rating the action compares against, and the ONLY source of it. Null means the action
     * is not offered, which is also how a host withholds it: ApprovePpaAction and the inventory mixin
     * both pass null for the CARFAX tester role.
     *
     * ⚠ The host decides, not this component, and that is the whole point rather than a preference.
     * This used to fall back to selectedMakeModel.lookupResult.carFaxRating when the prop was null -
     * which meant a tester who had just run a VIN decode got the button anyway, because the fallback
     * read straight past the host's withholding. The hosts' own carfaxRatingForMatch already walks
     * both sources, so the fallback was redundant as well as wrong.
     */
    carfaxRating: {
      type: Object,
      default: null
    },
    /*
     * What the VIN decoder returned, stored on the record when the VIN was decoded. Host-supplied for
     * the same reason carfaxRating is: this component never fetches, and the decode a selector runs
     * for its own pop-fills is not the record's answer.
     */
    vinDecodeName: {
      type: String,
      default: ''
    },
    vinDecodeDetails: {
      type: String,
      default: ''
    },
    /*
     * Whether the host wants the CARFAX comparison shown. It is the HOST'S OWN FLAG passed straight
     * down, deliberately - one flag, two presentations.
     *
     * ⚠ The comparison has to appear INSIDE this modal when the modal is open. It used to be raised
     * as a second b-modal by the host, a sibling of this one at the same z-index (main.scss gives
     * every .modal.is-active 9999), so document order alone decided and the host's prompt painted on
     * top. The footer's own CARFAX-match button made that certain rather than occasional: it cannot
     * be clicked unless this modal is already open. The host keeps its modal for the case this one is
     * closed, which is the flow where "Yes" is what opens it.
     */
    showCarfaxComparison: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isConfiguring: false,
      //⚠ Declared, not assigned into existence - an undeclared property is not reactive on Vue 2 and
      //the close handler would read stale text. vue-multiselect discards its own search on close, so
      //this is the only record of what was typed by the time onModelListClosed runs.
      lastModelSearch: '',
      /*
       * Where the pop-fill chips' values came from, for their tooltips.
       *
       * ⚠ The chips were built for the VIN decode and said so in eight hardcoded tooltips. They now
       * also carry CARFAX values, arrived at from the View Inventory comparison - and a chip whose
       * whole job is "trust this value" must not misname its own source.
       */
      offeredValueSource: 'vindecode',
      selectedLocateType: null,
      selectedMake: null,
      selectedYear: null,
      selectedModel: null,
      selectedStyle: null,
      selectedTrim: null,
      selectedVinNo: '',
      selectedSource: null,
      selectedWholeSaler: null,
      selectedCustomer: null,
      purchaseCost: null,
      askingPrice: null,
      usage: null,
      selectedBank: null,
      goodUntilDate: null,
      isDamaged: false,
      accNo: null,
      payoffAmount: null,
      selectedTitleStatus: null,
      warrantyExpirationDate: null,
      localTrims: [],
      localStyles: [],
      //⚠ The model and year lists are COMPUTED, so `this.models.push(...)` and
      //`this.productionYears.push(...)` - which onFillModel and setMakeModelFromCarfaxData both did -
      //do not survive the next invalidation. These are the trims/styles treatment applied to the
      //other two lists, so a value that carfax or the VIN decode found but the catalogue does not
      //hold can actually be selected rather than vanishing.
      localModels: [],
      localProductionYears: [],
      titleFiles: [],
      damageFiles: [],
      damageNote: '',
      originalSelectedExteriorColor: '',
      originalSelectedInteriorColor: '',
      selectedExteriorColor: '',
      selectedInteriorColor: '',
      selectedAltExteriorColor: [],
      selectedAltInteriorColor: [],
      selectedTransmissionType: null,
      altInteriorColors: [],
      altExteriorColors: [],
      selectedTags: [],
      availableTags: [],
      notes: '',
      vinDecodeErrors: [],
      selectedMakeModel: {
        makeId: null,
        selectedMake: null,
        modelId: null,
        selectedModel: null,
        productionYear: null,
        style: '',
        trim: '',
        exteriorColor: '',
        interiorColor: '',
        locateType: null,
        altExteriorColors: [],
        altInteriorColors: [],
        tags: [],
        notes: '',
        customMakeModel: '',
        cpoAllowed: false,
        autoCpoStatus: '',
        transmissionType: ''
      },
      isDamageStatusActive: false,
      isAccessoryStatusActive: false,
      showForm: true,
      isMobile: window.innerWidth <= 700,
      showReport: false,
      showCarFaxReport: false,
      isModelInfoCollapsed: false,
      //The model the record arrived on. Held so it stays offered even after it is retired from the
      //catalogue - see onSave, which blocks a NEWLY chosen deleted model but not this one.
      seededModelId: null,
      //Whether the inline model-choice prompt has a row ticked, so its Apply can be disabled.
      inlineCarfaxChoiceApplyable: false,
      //The learned CARFAX mappings for the description on screen - see fetchCarfaxLearning.
      carfaxLearnedMappings: [],
      carfaxLearningPending: false,
      //Numbers each fetch; only the latest one's reply is applied - see fetchCarfaxLearning.
      carfaxLearningSeq: 0,
      decodedProductionYear: undefined,
      decodedMake: undefined,
      decodedModel: undefined,
      modelError: false,
      yearError: '',
      isUploadingFile: false,
      fileUploadCount: 0,
      //Whether the selector was opened purely to show the CARFAX comparison - see openForCarfaxComparison.
      openedForCarfaxComparisonOnly: false,
      decodedTrim: undefined,
      decodedStyle: undefined,
      decodingVin: false,
      //The VIN the last decode ran against, so clicking twice does not pay for the same answer twice.
      lastDecodedVin: ''
    }
  },
  computed: {
    ...mapVehicleMakeGetters(['allVehicleMakes']),
    ...mapDealerGetters(['getCurrentDealer']),
    ...mapConfigGetters(['definedTypes']),
    ...mapUserGetters(['currentUser']),
    /*
     * ⚠ The shared predicate, not a local copy. Four screens already gate on it and the comment on
     * the util records what a fourth duplicate cost. It fails CLOSED - true while roles are still
     * loading - which is what this gate wants: withhold the report text until the user is known.
     */
    hasCarfaxTesterRole: function () {
      return hasCarfaxTesterRole(this.currentUser)
    },
    allowedSourceTypes: function () {
      let filtered = this.definedTypes.allowedSourceTypes.options.filter((x) => x.id !== 0)
      if (!this.selectedLocateType) {
        return filtered
      } else {
        if (this.selectedLocateType.id === 0) {
          return this.definedTypes.allowedSourceTypes.options
        } else {
          return filtered
        }
      }
    },
    purchasePriceRequired: function () {
      return this.isLocated
    },
    showTitleStatus: function () {
      if (!this.selectedLocateType) {
        return true
      } else {
        if (this.selectedLocateType.id === 0) {
          return !(this.selectedSource && this.selectedSource.id === this.definedTypes.allowedSourceTypes.Factory)
        } else {
          return true
        }
      }
    },
    titleStatusMediaLabel: function () {
      if (this.isTitlePayOff) return 'Payoff Info'
      if (this.isTitleLost) return 'Lost Title Affidavit'
      return 'Title (Front)'
    },
    titleStatusRequired: function () {
      let isFactorySource = this.selectedSource && this.selectedSource.id === this.definedTypes.allowedSourceTypes.Factory
      return !isFactorySource
    },
    isTitleReceived: function () {
      return this.selectedTitleStatus && (this.selectedTitleStatus.id === this.definedTypes.titleStatusOptions.Yes || this.selectedTitleStatus.id === this.definedTypes.titleStatusOptions.PayOff || this.selectedTitleStatus.id === this.definedTypes.titleStatusOptions.Lost)
    },
    isTitlePayOff: function () {
      return this.selectedTitleStatus && this.selectedTitleStatus.id === this.definedTypes.titleStatusOptions.PayOff
    },
    isTitleLost: function () {
      return this.selectedTitleStatus && this.selectedTitleStatus.id === this.definedTypes.titleStatusOptions.Lost
    },
    isNewLocateType: function () {
      if (!this.selectedLocateType) {
        return false
      } else {
        return this.selectedLocateType.id === 0
      }
    },
    allowedTitleStatusOptions: function () {
      if (!this.selectedLocateType) {
        return this.definedTypes.titleStatusOptions.options
      } else {
        if (this.selectedLocateType.id === 0) {
          if (this.selectedSource && this.selectedSource.id !== this.definedTypes.allowedSourceTypes.Factory) {
            return this.definedTypes.titleStatusOptions.options.filter((x) => x.id !== this.definedTypes.titleStatusOptions.Lost && x.id !== this.definedTypes.titleStatusOptions.PayOff)
          } else {
            return this.definedTypes.titleStatusOptions.options
          }
        } else {
          return this.definedTypes.titleStatusOptions.options
        }
      }
    },
    historicalDamageDataAvailable: function () {
      return this.value && this.value.damageHistory && this.value.damageHistory.length > 0
    },
    historicalAccessoryDataAvailable: function () {
      return this.value && this.value.accessoryStatusHistory && this.value.accessoryStatusHistory.length > 0
    },
    usageRequired: function () {
      return false
    },
    mileageTitle: function () {
      if (this.stockType && this.stockType.name === 'Locate') {
        return this.isLocated === true ? 'Mileage' : 'Minimum Mileage'
      } else {
        return 'Mileage'
      }
    },
    showDealerMakes: function () {
      if (this.isValidForDealerMakes()) {
        return true
      }
      return false
    },
    availableLocateTypes: function () {
      if (this.stockType && this.stockType.name === 'Order') {
        return this.definedTypes.allowedLocateTypes.options.filter(x => x.id === this.definedTypes.allowedLocateTypes.New)
      }
      return this.definedTypes.allowedLocateTypes.options
    },
    availableDealerMakes: function () {
      let sellsMakeIds = this.getCurrentDealer.sellsVehicleMakes.map((y) => y.vehicleMakeId)
      let result = this.allVehicleMakes.filter((x) => sellsMakeIds.includes(x.id))
      return result
    },
    makes: function () {
      return this.allVehicleMakes
    },
    //Newest first, and derived from the same selectable set as the model list so the two cannot
    //disagree. Previously unsorted, over a copy the selectedMake watcher produced with an in-place
    //lodash reverse - see utils/vehicle_make_version for the whole story.
    productionYears: function () {
      const years = sortedProductionYears(this.selectedMake ? this.selectedMake.versions : [], this.seededModelId)
      //Appended rather than merged into the sort: a year the catalogue does not hold is by definition
      //not one of the ordinary choices, so it belongs at the bottom beside the out-of-range ones.
      return years.concat(this.localProductionYears.filter((x) => years.indexOf(x) === -1))
    },
    /*
     * The free-text model name the record is carrying, or '' once a real catalogue model is chosen.
     *
     * ⚠ DERIVED, never passed in. That is what makes this cover three cases with one computed: a VIN
     * decode that just missed, a carfax value that missed, and an EXISTING record opened for edit -
     * which is how records already filed under a group repair themselves as operators touch them,
     * with no data migration. Stashing it on selectedMakeModel instead would put a new key on an
     * object that flows through six other screens.
     */
    unresolvedModelName: function () {
      if (this.selectedModel && this.selectedModel.id > 0) return ''
      if (this.selectedModel && this.selectedModel.name) return this.selectedModel.name
      if (this.selectedMakeModel && this.selectedMakeModel.customModelName) return this.selectedMakeModel.customModelName
      if (this.decodedModel && !(this.decodedModel.id > 0) && this.decodedModel.name) return this.decodedModel.name
      return ''
    },
    /*
     * ⚠ The chosen model is not a catalogue row. A catalogue model carries a real
     * VehicleMakeVersion id; a free-text one - from a VIN decode or carfax value the catalogue does
     * not hold - is built as { id: -1, name }, and EditInventory sends vehicleMakeVersionId as null
     * for any id below 1. Such a vehicle saves only through customModelName and never appears in
     * model reporting, which is what this warns the operator about.
     *
     * ⚠ `id > 0`, not `id === -1` - the house test everywhere on this path (unresolvedModelName,
     * onSelectedModel, trims, styles), and it covers 0, null and undefined uniformly.
     */
    showModelNotInCatalogue: function () {
      return !!this.selectedModel && !(this.selectedModel.id > 0)
    },
    /*
     * ⚠ Three states, not two. The modal head used to read `showInlineCarfaxComparison ? 'CARFAX' :
     * label`; the decode prompt is neither, and titling it "CARFAX" would name a report that has not
     * been fetched yet - the decode runs first.
     */
    modalTitle: function () {
      if (this.showDecodedModelChoice) return 'Vehicle model'
      return this.showInlineCarfaxComparison ? 'CARFAX' : this.label
    },
    models: function () {
      const catalogue = selectableModels(this.selectedMake ? this.selectedMake.versions : [], this.selectedYear, this.seededModelId)
      return catalogue.concat(this.localModels.filter((x) => !catalogue.some((y) => y.id === x.id)))
    },
    /*
     * ⚠ The catalogue arrays are defaulted because a version can legitimately arrive without them -
     * the spread throws "is not iterable" on undefined, and the template binds these to :options, so
     * the whole component stops rendering. applyMakeModelCascade now reads them too, where a throw
     * would abandon an accepted carfax apply half-applied rather than merely warn.
     */
    styles: function () {
      if (this.selectedModel && this.selectedModel.id > 0) {
        return [...(this.selectedModel.styles || []), ...this.localStyles]
      }
      return [...this.localStyles]
    },
    trims: function () {
      if (this.selectedModel && this.selectedModel.id > 0) {
        return [...(this.selectedModel.trims || []), ...this.localTrims]
      }
      return [...this.localTrims]
    },
    exteriorColors: function () {
      var result = []
      if (this.selectedMake && this.selectedMake.definedColors.length > 0) {
        const exteriorColors = this.$lodash.filter(this.selectedMake.definedColors, (x) => x.colorType === 'Exterior')
        result.push(...this.$lodash.map(exteriorColors, (y) => { return y.primaryColor + ' - ' + y.colorVariation })
        )
      }
      result.push(...this.definedTypes.defaultExteriorColors)
      if (this.originalSelectedExteriorColor && this.originalSelectedExteriorColor !== '' && !result.includes(this.originalSelectedExteriorColor)) {
        result.push(this.originalSelectedExteriorColor)
      }
      return this.$lodash.uniq(result)
    },
    interiorColors: function () {
      var result = []
      if (this.selectedMake && this.selectedMake.definedColors.length > 0) {
        const interiorColors = this.$lodash.filter(this.selectedMake.definedColors, (x) => x.colorType === 'Interior')
        result.push(...this.$lodash.map(interiorColors, (y) => { return y.primaryColor + ' - ' + y.colorVariation })
        )
      }
      result.push(...this.definedTypes.defaultInteriorColors)
      if (this.originalSelectedInteriorColor && this.originalSelectedInteriorColor !== '' && !result.includes(this.originalSelectedInteriorColor)) {
        result.push(this.originalSelectedInteriorColor)
      }
      return this.$lodash.uniq(result)
    },
    colorOverview: function () {
      var result = ''
      if (this.selectedExteriorColor) {
        result = 'Ext. Color (' + this.selectedExteriorColor + ')'
      }
      if (this.selectedInteriorColor) {
        result += '<br/> Int. Color (' + this.selectedInteriorColor + ')'
      }
      return result
    },
    smallColorOverview: function () {
      var result = ''
      if (this.selectedExteriorColor) {
        result = 'Ext. Color (' + this.selectedExteriorColor + ')'
      }
      if (this.selectedInteriorColor) {
        result += ' , Int. Color (' + this.selectedInteriorColor + ')'
      }
      return result
    },
    overview: function () {
      var result = ''
      if (this.selectedYear) {
        result = this.selectedYear
      }
      if (this.selectedMake) {
        result += ' ' + this.selectedMake.name
      }
      if (this.selectedModel) {
        result += ' ' + this.selectedModel.name
      }
      //⚠ Not appended when the model name already carries it as a whole word. This computed is the
      //label on the Select/Configure Vehicle button - the one the operator actually reads - and once
      //the carfax values are applied it holds model "296 GTS" with trim "GTS", so it read
      //"2024 Ferrari 296 GTS GTS Convertible" on screen. Same suppression as the reconcile prompt's
      //two builders and updateCustomMakeModel; this is the fourth and last place the pair is joined.
      if (this.selectedTrim && !modelNameCarriesTrim(this.selectedModel ? this.selectedModel.name : '', this.selectedTrim)) {
        result += ' ' + this.selectedTrim
      }
      if (this.selectedStyle) {
        result += ' ' + this.selectedStyle
      }
      if (result === '') {
        result = 'Click to configure vehicle'
      }
      return result
    },
    allowedTransmissionTypes: function () {
      return this.definedTypes.transmissionTypes
    },
    vehicleAge: function () {
      if (!this.selectedYear || this.selectedYear === 0) {
        return -1
      } else {
        return moment().year() - (+this.selectedYear)
      }
    },
    askingPriceLabel: function () {
      if (this.showAlternateColors === true) {
        return 'Approx. Sale Price/MSRP'
      }
      return 'Approx. Sale Price/MSRP'
    },
    carFaxReportUrl: function () {
      let result = 'https://www.carfaxonline.com/cfm/Display_Dealer_Report.cfm?partner=VAU_0&UID=' + this.getCurrentDealer.carFaxUserName + '&vin=' + this.selectedVinNo
      return result
    },
    autoCheckEnabled: function () {
      if (this.currentDealer) {
        return this.currentDealer.autoCheckEnabledStatus
      }
      return false
    },
    damageTitle: function () {
      if (this.autoCheckEnabled) {
        return 'Known damage or bad Carfax/Autocheck'
      }
      return 'Known damage or bad Carfax'
    },
    /*
     * The CARFAX overwrite note on this record, if it has one, with the values it recorded parsed out.
     *
     * ⚠ That note is the ONLY durable record of what the vehicle held before CARFAX was accepted. No
     * VIN-decode note exists anywhere - not in code, and not on any of the 43,784 inventory records.
     * The attributes are rewritten in place with no previous-value column, custom_make_model is
     * blanked on reconciliation, and on a catalogue hit the decoder's raw strings are dropped without
     * ever being stored. So a record reconciled before that note shipped has nothing to restore from,
     * and no backfill is possible.
     */
    carfaxOverwriteNote: function () {
      return findCarfaxOverwriteNote(this.inventoryNotes)
    },
    vinNoForDecode: function () {
      return String(this.vinNo || '').trim()
    },
    /*
     * ⚠ NOT when showVinSelector is on. Those hosts already render vin-no-selector, which carries its
     * own barcode decode button - a second one in the same modal is a duplicate affordance rather
     * than a convenience.
     *
     * ⚠ === 17, mirroring VinNoSelector.isValid. The field itself accepts 11-17 characters, but a
     * partial VIN decodes to a different vehicle, so the decode only ever fires on a complete one.
     */
    canDecodeVin: function () {
      return !this.showVinSelector && this.vinNoForDecode.length === 17
    },
    //The rating the check compares against - see the prop, which is its only source.
    carfaxRatingToCompare: function () {
      return this.carfaxRating || null
    },
    /*
     * ⚠ Offered only when there is a rating to compare against. An action that promises a comparison
     * it has no data for is the same failure it exists to fix, in a new costume.
     */
    canCheckCarfaxMatch: function () {
      const rating = this.carfaxRatingToCompare
      return !!(rating && rating.make)
    },
    /*
     * Whether the record as it stands right now agrees with the report: 'matches', 'differs', or
     * 'unknown' when the comparison could not run.
     *
     * ⚠ Run against THIS component's own values rather than the host's, so the colour answers for
     * what is on screen - it turns green the moment the operator corrects the model, before anything
     * is saved. It cannot drift from what the click produces: fireOnChange emits every change
     * straight into the object the host compares, and both sides call the same buildCarfaxComparison.
     */
    /*
     * The comparison itself, built ONCE and shared by the icon colour and the inline table.
     *
     * ⚠ Shared deliberately. Two calls would be two objects built from the same inputs, and the
     * moment one caller passed something the other did not, the colour on the button and the rows in
     * the table could disagree about the same vehicle.
     */
    carfaxComparisonForDisplay: function () {
      if (!this.canCheckCarfaxMatch) return null
      return buildCarfaxComparison(this.allVehicleMakes, this.carfaxRatingToCompare, this.selectedMakeModel)
    },
    carfaxMatchState: function () {
      if (!this.canCheckCarfaxMatch) return 'unknown'
      return carfaxMatchOutcome(this.carfaxComparisonForDisplay)
    },
    /*
     * The CARFAX description whose learned mappings the open comparison needs, or null.
     *
     * ⚠ Null until the inline comparison would actually show, so a host that merely passes a rating -
     * Edit Inventory mounts this selector with one on every load - never fetches for nothing. The raw
     * halves CARFAX sent, as findLearnedMapping keys them; a placeholder series counts as none.
     */
    carfaxLearningRequest: function () {
      if (!this.showInlineCarfaxComparison) return null
      const carfax = this.carfaxComparisonForDisplay && this.carfaxComparisonForDisplay.carfaxMakeModel
      if (!carfax || !isMeaningfulValue(carfax.sourceModelName)) return null
      return { model: carfax.sourceModelName, series: isMeaningfulValue(carfax.sourceSeries) ? carfax.sourceSeries : '' }
    },
    carfaxLearningRequestKey: function () {
      const request = this.carfaxLearningRequest
      return request ? JSON.stringify([request.model, request.series]) : null
    },
    /*
     * What this dealership has previously made of the same CARFAX description, or null.
     *
     * ⚠ Keyed on sourceModelName / sourceSeries - the RAW halves CARFAX sent - not on the resolved
     * name. buildCarfaxComparison preserves those two precisely because the resolver joins them.
     *
     * ⚠ Year comes from the CARFAX rating, so the suggestion resolves onto the catalogue row for
     * THIS car's year rather than the year the mapping happened to be learned on.
     */
    carfaxLearnedSuggestion: function () {
      const comparison = this.carfaxComparisonForDisplay
      const carfax = comparison && comparison.carfaxMakeModel
      if (!carfax) return null

      return findLearnedMapping(
        this.carfaxLearnedMappings,
        carfax.selectedMake,
        carfax.sourceModelName,
        carfax.sourceSeries,
        carfax.productionYear)
    },
    /*
     * The comparison is shown here rather than by the host only while THIS modal is on screen.
     * Closed, or inline (useModal false, which carries no CARFAX-match button at all), the host's own
     * modal is the right presentation and this renders nothing.
     */
    /*
     * ⚠ DERIVED, never a mirrored copy of useModal. The four report handlers used to assign
     * this.useModal = false/true, mutating a PROP: Vue 2 warns, and because every host binds it with a
     * literal (ArrivalCapture, LocateDealCapture, ViewDeal and InventoryLocatedCaptureDetail pass false,
     * BookDealCapture passes true) the host's next re-render put it straight back - so the selector
     * reappeared around an open report. showReport and showCarFaxReport are set by those same handlers
     * and already carry the state, so the layout branches read this instead.
     */
    isReportOpen: function () {
      return this.showReport === true || this.showCarFaxReport === true
    },
    showInlineCarfaxComparison: function () {
      return this.showCarfaxComparison === true && this.useModal === true && this.isConfiguring === true && !!this.carfaxComparisonForDisplay
    },
    /*
     * ⚠ Renamed onto the keys CarfaxVehicleComparison expects. The comparison speaks
     * vehicleMakeDifferent/modelNameDifferent; the table speaks make/model. The hosts do this same
     * mapping inline in their own templates.
     */
    inlineCarfaxDifferences: function () {
      const differences = (this.carfaxComparisonForDisplay || {}).differences || {}
      return {
        make: differences.vehicleMakeDifferent,
        productionYear: differences.productionYearDifferent,
        model: differences.modelNameDifferent,
        trim: differences.trimDifferent,
        style: differences.styleDifferent
      }
    },
    /*
     * ⚠ Derived from the SAME predicate the hosts raise the prompt with - carfaxModelNameDiffers -
     * rather than taken as a prop. A second channel could disagree with the gate, and then the footer
     * would offer Apply for a model that needs none, or withhold it from one that does.
     */
    inlineCarfaxCanApply: function () {
      return carfaxModelNameDiffers(this.carfaxComparisonForDisplay)
    },
    /*
     * The two halves of the inline CARFAX view, as separate render conditions.
     *
     * ⚠ They used to be one section and one footer forked inline on inlineCarfaxCanApply. Splitting
     * them is what lets the model half use the CHOICE table while the field half keeps the
     * five-field diff - the field half has no model to choose, so the choice table would render
     * nothing there.
     */
    showInlineModelChoice: function () {
      return this.showInlineCarfaxComparison && this.inlineCarfaxCanApply
    },
    /*
     * ⚠ Hidden entirely when neither source said anything, rather than shown as a box of dashes.
     * Records decoded before the decode summary shipped have no stored decode, and a report predating
     * the extraction change has no heading - an empty evidence box on those reads as a broken feature.
     */
    showSourceEvidence: function () {
      /*
       * ⚠ WITHHELD FROM CARFAX TESTERS, and gated HERE rather than on each box, because the evidence
       * renders in two places - the configure form and the reconcile section - and a second gate is a
       * gate that drifts. This table is the one place the report's own wording is reproduced verbatim
       * rather than reduced to extracted fields, which is exactly what the tester scrub exists to keep
       * back; CarFaxControl, CarFaxReportControl and CarfaxProcessStatusHeader already withhold their
       * own CARFAX surfaces from the same role.
       *
       * The backend blanks these two strings in MakeSafe() as well, but only in Development, so this
       * is the gate that holds everywhere.
       */
      if (this.hasCarfaxTesterRole) return false

      const carfax = this.carfaxRating || {}
      return [carfax.vehicleName, carfax.vehicleDetails, this.vinDecodeName, this.vinDecodeDetails]
        .some((x) => !!x && String(x).trim() !== '' && String(x).trim().toLowerCase() !== 'n/a')
    },
    showInlineFieldDifferences: function () {
      return this.showInlineCarfaxComparison && !this.inlineCarfaxCanApply
    },
    inlineCarfaxMessage: function () {
      if (!this.inlineCarfaxCanApply) {
        const fields = readMakeModelFields(this.selectedMakeModel)
        return buildFieldDifferencesMessage(fields ? fields.model : '')
      }
      return buildReconcileMessage()
    },
    /*
     * ⚠ THE COLOUR NO LONGER PREDICTS THE CLICK, and restoring that idea is the single easiest way to
     * break this again. It reports a fact about the RECORD - see carfaxMatchIconClass, which holds the
     * rule and its reasoning. What the click will do is the TOOLTIP's job, via carfaxMatchHoverText.
     *
     * ⚠ The lesson that produced the split is worth keeping, because it has now been relearned three
     * times: a colour that promises an action the click cannot perform is worse than no colour at all.
     * An operator once saw red on a record whose model was already right, clicked expecting to fix a
     * model, and got something else. The same mistake then reappeared in the tooltip - twice - which
     * is why the wording is keyed on the predicate the hosts actually gate the prompt on rather than
     * on anything inferred from the colour.
     */
    //⚠ Shared with EditInventory's own indicator, which answers the same question about the same
    //record from outside this modal. Kept in the util so the two colours cannot drift apart.
    carfaxMatchIconClass: function () {
      return carfaxMatchIconClass(this.carfaxComparisonForDisplay)
    },
    /*
     * The control is an icon with no label, so the hover text is the whole affordance - and now that
     * the icon carries a colour, it has to say what that colour means.
     */
    carfaxMatchHoverText: function () {
      return carfaxMatchHoverText(this.carfaxComparisonForDisplay)
    }
  },
  methods: {
    /*
     * The learned mappings for the description on screen. The options wait behind the loader until this
     * settles (see the inline sections), and a failure only costs the suggestion - it is optional, so it
     * is logged rather than toasted and the options still show.
     *
     * ⚠ Only the LATEST request's reply is applied - numbered, not keyed on the description. A slow reply
     * for a vehicle the operator has moved past would otherwise overwrite the current suggestion, and so
     * would one from before a close-and-reopen of the SAME description: that reply may predate the very
     * correction the reopen exists to show (vue review on #1589).
     */
    fetchCarfaxLearning: function () {
      const request = this.carfaxLearningRequest
      const seq = ++this.carfaxLearningSeq
      this.carfaxLearnedMappings = []
      if (!request) {
        this.carfaxLearningPending = false
        return
      }
      this.carfaxLearningPending = true
      carfaxModelMappingService.carfaxModelMappingsFor(request.model, request.series)
        .then((data) => {
          if (this.carfaxLearningSeq !== seq) return
          this.carfaxLearnedMappings = (data && data.results) || []
          this.carfaxLearningPending = false
        }, (error) => {
          if (this.carfaxLearningSeq !== seq) return
          console.log(error)
          this.carfaxLearningPending = false
        })
    },
    onDecodeVinClick: function (ev) {
      this.decodeVinForPopFills(!!(ev && ev.ctrlKey))
    },
    /*
     * The comparison itself belongs to the host, which owns the reconcile latches, the prompt and the
     * toast - so this only asks for it.
     *
     * ⚠ An earlier version of this comment said the selector "has no access to allVehicleMakes".
     * That was never true - it is mapped at the top of this computed block to build the model list -
     * and the colour on the button above is computed from it.
     */
    onCheckCarfaxMatchClick: function () {
      this.$emit('carfax-match-requested')
    },
    /*
     * Reconcile the fields the model prompt does not cover - trim, style and year.
     *
     * ⚠ OFFERS, never applies, which is the same contract offerDecodedMakeModel keeps and the reason
     * this is a separate button from Yes. Nothing here writes to selectedTrim/selectedStyle/selectedYear
     * or to the bound selectedMakeModel; each value lands as a pop-fill chip the operator still clicks.
     * The model is deliberately untouched - it already agrees, which is why this half exists.
     *
     * ⚠ Only the fields the comparison FLAGGED are armed. Arming a field that already matches would
     * put a chip on the screen offering the value the record already holds.
     *
     * ⚠ CARFAX's trim is `series`, not `trim` - the report carries the model and the trim as separate
     * fields, and buildCarfaxComparison reads `series` for the same reason.
     */
    /*
     * The inline model-choice Apply. Emits the row the operator TICKED, not a bare signal.
     *
     * ⚠ The host handler is the same one the bare `carfax-apply` used to reach - only the payload
     * changes. Emitting nothing is precisely what let the host fall through to writing CARFAX's
     * model group as free text; the chosen row is what gives the record a catalogue link.
     */
    onInlineCarfaxModelApply: function (chosenRow) {
      this.$emit('carfax-apply', chosenRow && chosenRow.model)
    },
    /*
     * Cancel on either inline CARFAX footer.
     *
     * ⚠ IT CLOSES THE SELECTOR TOO when the selector was opened only to host the comparison. Leaving
     * it open there showed the operator a make/model form retitled "Select/Configure Vehicle" that they
     * never opened - which reads as the Cancel button opening something rather than closing it.
     *
     * ⚠ But NOT when the operator opened the selector themselves and raised the comparison from its
     * own footer. There the form underneath is what they were working in, and closing it would throw
     * away their half-finished edit. That is the behaviour ApprovePpaAction and NewInventory rely on.
     */
    onDismissInlineCarfax: function () {
      this.$emit('carfax-dismiss')
      if (!this.openedForCarfaxComparisonOnly) return

      this.openedForCarfaxComparisonOnly = false
      this.isConfiguring = false
    },
    onReconcileInlineCarfaxFields: function () {
      //The suggestion is still loading - nothing to reconcile against yet (the button is disabled too).
      if (this.carfaxLearningPending) return
      const rating = this.carfaxRatingToCompare || {}
      const differences = this.inlineCarfaxDifferences

      //⚠ These chips are armed from the CARFAX report, so they must say so. Omitted, they inherited
      //whatever the instance last happened to be told - the default 'vindecode' on a fresh selector,
      //which labelled CARFAX values as a catalogue lookup.
      this.offeredValueSource = 'the CARFAX report'

      if (differences.productionYear) {
        const productionYear = rating.productionYear ? +rating.productionYear : null
        if (isPlausibleProductionYear(productionYear)) {
          this.decodedProductionYear = productionYear
          this.addToUniqueList(productionYear, this.localProductionYears)
        }
      }

      //⚠ Only what the record's model does not already carry: the resolver folds the series into the
      //model name, so the raw series offered "911 Targa 4 GTS" the trim "Targa 4 GTS" (FC2479).
      const trimOffer = differences.trim ? carfaxSeriesTrimOffer(rating.series, this.selectedMakeModel) : ''
      if (trimOffer) {
        this.decodedTrim = trimOffer
        this.addToUniqueList(trimOffer, this.localTrims)
      }

      if (differences.style && isMeaningfulValue(rating.style)) {
        this.decodedStyle = rating.style
        this.addToUniqueList(rating.style, this.localStyles)
      }

      /*
       * ⚠ Clear the flag WITHOUT closing. Reconcile has just armed pop-fill chips on the fields
       * behind the comparison, so the form is now exactly where the operator needs to be - unlike
       * Cancel, which leaves nothing to look at.
       */
      this.openedForCarfaxComparisonOnly = false
      //Close the comparison so the chips it just armed are visible on the fields behind it.
      this.$emit('carfax-dismiss')
    },
    /*
     * Decode the record's VIN and arm the pop-fill chips with what comes back.
     *
     * ⚠ getCarFax and getAutoCheck are BOTH sent explicitly false, and that is not defensive noise.
     * VinModel.GetCarFax defaults to TRUE server-side, so a request that merely OMITS the flag buys a
     * CARFAX report, and getAutoCheck bills the dealer's own Experian account. VinNoSelector and
     * VinNoDecoder already send them false; this follows that precedent rather than inventing a
     * policy. What is left is the free spec decode.
     */
    decodeVinForPopFills: function (forceRun) {
      if (!this.canDecodeVin) return Promise.resolve()
      //⚠ No cache exists client-side or server-side, so a repeat click is two DB queries plus two
      //live HTTP lookups for an answer already on screen. Mirrors VinNoSelector's own same-VIN memo.
      if (!forceRun && this.lastDecodedVin === this.vinNoForDecode) return Promise.resolve()

      this.decodingVin = true
      this.lastDecodedVin = this.vinNoForDecode
      this.vinDecodeErrors = []
      return inventoryService.decode({
        //⚠ The record's own id, so VinValidator does not refuse the vehicle for already being
        //itself. See the inventoryId prop.
        id: this.inventoryId || null,
        dealerId: this.getCurrentDealer ? this.getCurrentDealer.id : null,
        vinNo: this.vinNoForDecode,
        generate: false,
        brandId: null,
        getAutoCheck: false,
        getCarFax: false
      }).then((response) => {
        this.decodingVin = false
        if (!response || response.errors || !response.lookupResult) {
          this.vinDecodeErrors = (response && response.errors) || []
          return
        }
        this.offerDecodedMakeModel(response.lookupResult)
      }).catch((error) => {
        this.decodingVin = false
        this.vinDecodeErrors = (error && error.errors) || []
      })
    },
    /*
     * Arm the four pop-fill chips from a decode result.
     *
     * ⚠ OFFER, never apply. This deliberately does not call applyCarfaxMakeModel: the ask was to
     * populate the pop-fill fields, so the operator still clicks each value. Nothing here writes to
     * selectedMake/Model/Trim/Style or to the bound selectedMakeModel.
     *
     * ⚠ Deliberately NOT routed through setMakeModelFromCarfaxData, despite the overlap. That method
     * expects an already-resolved carfax shape and opens with an unguarded selectedMake.id, and it
     * assigns selectedMake when none is chosen - an apply, which is the one thing this must not do.
     */
    offeredValueTooltip: function (fieldName) {
      return buildOfferedValueTooltip(fieldName, this.offeredValueSource)
    },
    /*
     * Open the selector with the CARFAX report's values armed as pop-fill chips.
     *
     * ⚠ OFFER, never apply - the same contract as the decode path it delegates to. Dean, 20 Sep:
     * arriving from View Inventory's comparison must "not just update the record", it must show the
     * selector with the suggested changes pop-filled.
     *
     * ⚠ Delegates rather than duplicates, and that is what makes the group case right for free:
     * offerDecodedMakeModel withholds the model chip and raises the choice prompt when the catalogue
     * can disambiguate, so a CARFAX model of "296" offers 296 GTB / 296 GTS instead of one click that
     * files "296" as free text.
     *
     * ⚠ rating.series lands in BOTH slots on purpose: as `series` it resolves the model to its
     * catalogue row, and as `trim` it arms the trim chip. That is the mapping
     * utils/carfax_make_model_comparison.js already uses (:98 compares series against trim, :159
     * resolves on it) - not a new one invented here.
     */
    /*
     * Open the selector so its inline CARFAX comparison can be read. Offers nothing.
     *
     * ⚠ NOT offerCarfaxMakeModel. This is the state where the MODEL already agrees and only trim,
     * style, year or make differs - there is nothing to offer, only something to show. Offering would
     * push CARFAX's values at an operator whose model is already right, and would also relabel the
     * value chips as coming from the report.
     *
     * ⚠ WHY IT HAS TO EXIST. showInlineCarfaxComparison requires isConfiguring, so the host's
     * showCarfaxComparison flag renders nothing on its own. Every other caller raises the comparison
     * from the selector's OWN footer, where it is already open - only Edit Inventory's icon sits
     * outside it, and there the click did nothing at all.
     */
    openForCarfaxComparison: function () {
      /*
       * ⚠ REMEMBER WHY IT OPENED. Every other caller raises the comparison from INSIDE a selector the
       * operator opened themselves, so dismissing it correctly reveals the form underneath. This caller
       * is the Edit Inventory icon, which opens the selector for no other purpose than to host the
       * comparison - so Cancel there must close the lot rather than strand the operator in a make/model
       * form they never asked for. Dean, 21 Sep.
       */
      this.openedForCarfaxComparisonOnly = true
      this.isConfiguring = true
      return true
    },
    offerCarfaxMakeModel: function (carFaxRating) {
      if (!carFaxRating || !carFaxRating.make) return false

      this.isConfiguring = true
      //⚠ The source travels WITH the arming rather than being set beside it. Assigned here instead, it
      //outlived the offer: the next VIN decode on this same instance re-armed the chips and they kept
      //claiming CARFAX provenance, because nothing ever set it back.
      this.offerDecodedMakeModel({
        make: carFaxRating.make,
        model: carFaxRating.model,
        series: carFaxRating.series,
        trim: carFaxRating.series,
        style: carFaxRating.style,
        productionYear: carFaxRating.productionYear
      }, 'the CARFAX report')
      return true
    },
    /*
     * ⚠ source DEFAULTS to the decode, because that is who calls this without saying: the selector's
     * own Decode VIN button. Every caller that arms chips from somewhere else has to name it.
     */
    offerDecodedMakeModel: function (lookupResult, source = 'vindecode') {
      this.offeredValueSource = source
      //Held so decodedModelChoice can see a decode the record never receives - see decodedLookupResult.
      this.beginDecodedModelChoice(lookupResult)
      //⚠ The endpoint sends productionYear as a STRING, and resolveCarfaxMakeModel matches the year
      //with ===, so without this coercion nothing would ever resolve.
      let productionYear = lookupResult.productionYear ? +lookupResult.productionYear : null
      if (productionYear && isPlausibleProductionYear(productionYear)) {
        this.decodedProductionYear = productionYear
        this.addToUniqueList(productionYear, this.localProductionYears)
      }

      if (lookupResult.trim && String(lookupResult.trim).trim() !== '') {
        this.decodedTrim = lookupResult.trim
        this.addToUniqueList(lookupResult.trim, this.localTrims)
      }

      if (lookupResult.style && String(lookupResult.style).trim() !== '') {
        this.decodedStyle = lookupResult.style
        this.addToUniqueList(lookupResult.style, this.localStyles)
      }

      let foundMake = lookupResult.make
        ? this.makes.find((x) => x.name && x.name.toUpperCase() === String(lookupResult.make).toUpperCase())
        : null
      //A make the catalogue does not hold has no selectable model under it either, so the year, trim
      //and style above stand alone and nothing further is offered.
      if (!foundMake) return
      this.decodedMake = foundMake

      /*
       * ⚠ The VIN decode returns make/model/trim/style and has NO series field, so this is blank on
       * that path. CARFAX does have one, and it is the half that resolves the group: "296" + "GTS"
       * is the catalogue row "296 GTS". Dropping it would send every CARFAX arrival to the choice
       * prompt when it could have resolved outright.
       */
      let resolved = resolveCarfaxMakeModel(foundMake, {
        model: lookupResult.model,
        series: lookupResult.series || '',
        style: lookupResult.style,
        productionYear: productionYear
      })

      if (resolved.resolved) {
        this.decodedModel = resolved.selectedModel
        this.addToUniqueList(resolved.selectedModel, this.localModels)
        return
      }

      /*
       * ⚠ Withheld when the catalogue can disambiguate the group. The chip is one click from filing
       * "812" as the model - the same value the decode itself no longer writes - so arming it here
       * would put a second, contradictory answer next to the prompt that is about to raise. The group
       * name is still reachable: it is a row in that prompt, chosen deliberately rather than filled in.
       *
       * ⚠ Cleared, not merely skipped. A second decode in the same session would otherwise leave the
       * previous decode's chip on screen beside the new prompt.
       *
       * ⚠ Gated on decodedModelChoice, NOT on decodedModelNeedsChoice directly, and the difference is
       * load-bearing: the choice is null once a model is already selected, and there the chip is still
       * the right answer - it offers, and the operator has something to compare it against.
       *
       * ⚠ It also RAISES here. This is the component's own decode button, whose result never reaches
       * the record, so offerModelListWhenNotInCatalogue - which hangs off the host's decode - never
       * runs for it. Without this the chip would be withheld and no prompt would replace it.
       */
      if (this.raiseDecodedModelChoice()) {
        this.decodedModel = undefined
        return
      }

      //⚠ The decode routinely names the model GROUP - "296" where the catalogue holds "296 GTS" -
      //which is meeting change 1's whole subject. Offering it as a chip keeps it visible instead of
      //dropping it, and onFillModel already handles a row the catalogue did not supply.
      if (lookupResult.model && String(lookupResult.model).trim() !== '') {
        this.decodedModel = { id: -1, name: lookupResult.model, productionYear: productionYear, trim: '', style: '' }
        this.addToUniqueList(this.decodedModel, this.localModels)
      }
    },
    onFillProductionYear: function () {
      //⚠ The decoded year comes from a Carfax PDF extraction and is not validated on the way in, so a
      //malformed report can offer a nonsense year as a one-click pop-fill.
      if (!isPlausibleProductionYear(this.decodedProductionYear)) {
        this.yearError = `Carfax reported ${this.decodedProductionYear}, which is not a usable production year.`
        return
      }
      this.yearError = ''
      //⚠ These two used to set the local ref and NOTHING ELSE - no selectedMakeModel write, no
      //fireOnChange - while their onFillTrim/onFillStyle siblings did both. Nothing watches
      //selectedYear or selectedModel, and onSave reads selectedMakeModel, so clicking either chip
      //and pressing Done saved nothing at all. Routing through the onSelected* handlers is what
      //makes them persist, and keeps one definition of what selecting a year or a model means.
      this.addToUniqueList(this.decodedProductionYear, this.localProductionYears)
      this.selectedYear = this.decodedProductionYear
      this.onSelectedYear(this.decodedProductionYear)
    },
    /*
     * The escape hatch. The filter is a default, not a cage - the decode can be wrong about the
     * group, and an operator who needs a model outside it must not be trapped.
     */
    onFillModel: function () {
      //⚠ Not `this.models.push(...)` - models is a COMPUTED, so that push did not survive the next
      //invalidation and the decoded model was never actually offered in the list.
      this.addToUniqueList(this.decodedModel, this.localModels)
      this.selectedModel = this.decodedModel
      this.onSelectedModel(this.decodedModel)
    },
    onStartOver: function () {
      this.$emit('restore')
      this.isConfiguring = false
    },
    onValidate: function () {
      if (this.dataVvScope && this.dataVvScope !== '') {
        this.validateBeforeSubmitScoped(this.dataVvScope)
      } else {
        this.validateBeforeSubmit()
      }
    },
    onVinDecodeComplete: function (selectedMakeModel) {
      this.vinDecodeErrors = []
      this.initFromValue(selectedMakeModel)
    },
    onValidationError: function (errors) {
      this.vinDecodeErrors = errors
    },
    onNoteChanged: function () {
      this.selectedMakeModel.notes = this.notes
    },
    packTitleFiles: function () {
      if (this.titleFiles.length > 0) {
        var titleFiles = this.titleFiles.map((x) => {
          return {
            uploadName: x.name,
            uploadSize: x.size,
            uniqueId: x.uniqueId,
            type: x.type,
            classType: this.isTitlePayOff ? 'PayOffInfo' : (this.isTitleLost ? 'LostTitleAffidavit' : 'TitleFront')
          }
        })
        return titleFiles
      }
      return []
    },
    packDamageFiles: function () {
      if (this.damageFiles.length > 0) {
        var damageFiles = this.damageFiles.map((x) => {
          return {
            uploadName: x.name,
            uploadSize: x.size,
            uniqueId: x.uniqueId,
            type: x.type,
            classType: 'Damage'
          }
        })
        return damageFiles
      }
      return []
    },
    getIconByLastDamageStatus: function () {
      let classToAttach = {}
      if (this.value.damageHistory.length > 0) {
        let lastDamageStatus = this.value.damageHistory[0]
        if (lastDamageStatus.isDamaged === true) {
          classToAttach['fa-exclamation-triangle'] = true
          classToAttach['has-text-danger'] = true
        } else {
          classToAttach['fa-check-circle'] = true
          classToAttach['has-text-success'] = true
        }
      }
      return classToAttach
    },
    getLastDamageDescription: function () {
      var result = 'No damage recorded'
      if (this.value.damageHistory.length > 0) {
        let lastDamageStatus = this.value.damageHistory[0]
        result = lastDamageStatus.notes !== null ? lastDamageStatus.notes : 'No damage recorded'
      }
      return result
    },
    getIconByLastAccessoryStatus: function () {
      let classToAttach = {}
      if (this.value.accessoryStatusHistory.length > 0) {
        let lastAccessoryStatus = this.value.accessoryStatusHistory[0]
        if (lastAccessoryStatus.isGood === true) {
          classToAttach['fa-check-circle'] = true
          classToAttach['has-text-success'] = true
        } else {
          classToAttach['fa-exclamation-triangle'] = true
          classToAttach['has-text-danger'] = true
        }
      }
      return classToAttach
    },
    getLastAccessoryStatusDescription: function () {
      var result = 'All accessories provided'
      if (this.value.accessoryStatusHistory.length > 0) {
        let lastAccessoryStatus = this.value.accessoryStatusHistory[0]
        result = lastAccessoryStatus.notes !== null ? lastAccessoryStatus.notes : result
      }
      return result
    },
    onShowHistory: function () {
      alert('Coming shortly the ability to see historical damage, accesory status and in the future service requests...')
    },
    isValidForDealerMakes: function () {
      if (this.getCurrentDealer.sellsVehicleMakes.length === 0) {
        return false
      }
      if (this.isDealerMake()) {
        return true
      }
      return false
    },
    isDealerMake: function () {
      if (!this.selectedMake) {
        return false
      }
      let dealerMake = this.getCurrentDealer.sellsVehicleMakes.find((x) => x.vehicleMakeId === this.selectedMake.id)
      return dealerMake !== undefined
    },
    titleCase: function (str) {
      return str.toLowerCase().split(' ').map(function (word) {
        return word.replace(word[0], word[0].toUpperCase())
      }).join(' ')
    },
    focusElement: function (elementName) {
      if (this.$refs && this.$refs[elementName] && this.$refs[elementName].$el) {
        let that = this
        that.$nextTick(() => {
          that.$refs[elementName].$el.focus()
        })
      }
    },
    onSave: function () {
      this.modelError = false
      /*
       * ⚠ Blocks a NEWLY chosen deleted model, not one the record already carried. This used to refuse
       * any selection whose name contained _DELETED - including the model the vehicle was booked on
       * before that model was retired - so such a record could not be saved at all from this screen,
       * even to change something unrelated to the model. The dropdown no longer offers deleted models,
       * so a new selection can only be deliberate; an inherited one must stay editable.
       */
      let selectedModel = this.selectedMakeModel ? this.selectedMakeModel.selectedModel : null
      let isNewlySelectedDeletedModel = selectedModel &&
        typeof selectedModel.name === 'string' &&
        selectedModel.name.indexOf('_DELETED') !== -1 &&
        selectedModel.id !== this.seededModelId
      if (isNewlySelectedDeletedModel) {
        this.modelError = true
        return
      }
      this.isConfiguring = false
      if (!this.useModal) {
        this.showForm = false
        eventBus.$emit('parent-make-model-onSave')
      }
      eventBus.$emit('parent-make-model-onSave-book-deal-capture')
      this.onChangesSaved()
    },
    updateCustomMakeModel: function () {
      //⚠ Cleared first. The reset used to live inside the year branch, so with no year selected this
      //method APPENDED to whatever was already there - and it is called after every one of the five
      //onSelected* handlers, so applying a whole vehicle in sequence concatenated the description
      //five times over.
      this.selectedMakeModel.customMakeModel = ''
      if (this.selectedYear) {
        this.selectedMakeModel.customMakeModel = this.selectedYear
      }
      if (this.selectedMake) {
        this.selectedMakeModel.customMakeModel += ' ' + this.selectedMake.name
      }
      if (this.selectedModel) {
        this.selectedMakeModel.customMakeModel += ' ' + this.selectedModel.name
      }
      //⚠ Not appended when the model name already carries it as a whole word, or this field reads
      //"2024 Ferrari 296 GTS GTS Convertible" - the catalogue row is named "296 GTS" and the trim is
      //"GTS". Same suppression as the reconcile prompt's two builders; this is the THIRD place the
      //pair is concatenated, and the only one the operator reads on the form itself.
      if (this.selectedTrim && this.selectedTrim.toLowerCase() !== 'base' &&
          !modelNameCarriesTrim(this.selectedModel ? this.selectedModel.name : '', this.selectedTrim)) {
        this.selectedMakeModel.customMakeModel += ' ' + this.selectedTrim
      }
      if (this.selectedStyle && this.selectedStyle.toLowerCase() !== 'base') {
        this.selectedMakeModel.customMakeModel += ' ' + this.selectedStyle
      }
    },
    makeAdded: function (data) {
      data = this.titleCase(data)
      let newMake = {id: -1, name: data, versions: [], definedColors: []}
      this.selectedMake = newMake
      this.selectedYear = null
      this.selectedModel = null
      this.selectedTrim = null
      this.selectedStyle = null
      this.selectedExteriorColor = ''
      this.selectedInteriorColor = ''
      this.selectedMakeModel.makeId = -1
      this.selectedMakeModel.selectedMake = null
      this.selectedMakeModel.customMakeName = data
      if (!this.makes.includes(newMake)) {
        this.makes.push(newMake)
      }
      this.updateCustomMakeModel()
      this.fireOnChange()
      this.focusElement('year')
    },
    yearAdded: function (data) {
      /*
       * ⚠ This is where the bad years came from. The field is taggable, so anything typed became the
       * year with no check at all - and because the year dropdown is built from the catalogue, one
       * typo then polluted it for that make permanently. Two live rows, "296 GTB" at 2034 and 2037,
       * were created exactly this way.
       *
       * ⚠ The old code also had a dead push: `if (this.productionYears.find(x => x === data))` was
       * inverted (push only when already present), and compared a STRING from the tag event against a
       * list of NUMBERS, so it never matched. It pushed into a computed in any case, which does not
       * survive the next invalidation. Removed rather than repaired - productionYears now derives the
       * list itself.
       */
      if (!isPlausibleProductionYear(data)) {
        this.yearError = `'${data}' is not a valid production year. Enter a year up to ${maxProductionYear()}.`
        this.selectedYear = null
        return
      }

      this.yearError = ''
      this.selectedYear = data
      this.selectedModel = null
      this.selectedTrim = null
      this.selectedStyle = null
      this.selectedMakeModel.productionYear = +data
      this.updateCustomMakeModel()
      this.fireOnChange()
      this.focusElement('model')
    },
    modelAdded: function (data) {
      data = this.titleCase(data)
      let newModel = {id: -1, name: data, style: ''}
      this.selectedModel = newModel
      this.selectedTrim = null
      this.selectedStyle = null
      this.selectedMakeModel.modelId = -1
      this.selectedMakeModel.selectedModel = null
      this.selectedMakeModel.customModelName = data
      //⚠ localModels, not the computed. `this.models.push(...)` did not survive the next
      //invalidation, so a model the operator typed in was selected but never listed. The
      //`includes(newModel)` test could never match either - newModel is a fresh object literal.
      this.addToUniqueList(newModel, this.localModels)
      this.updateCustomMakeModel()
      this.fireOnChange()
      /*
       * ⚠ ASK AFTER WRITING, not instead of it. vue-multiselect puts the tag row at index 0 with the
       * pointer already on it, so Enter files the free text even with "812 Superfast" visible below -
       * the write is the behaviour the operator expects and the prompt is the second chance. Committed
       * is true here, so Cancel needs to do nothing to keep it.
       */
      this.lastModelSearch = ''
      if (this.raiseTypedModelChoice(data, true)) return
      this.focusElement('trim')
    },
    //Held so the close handler can see text vue-multiselect is about to discard.
    onModelSearchChanged: function (searchText) {
      this.lastModelSearch = searchText
    },
    /*
     * The operator typed something and clicked away.
     *
     * ⚠ deactivate() CLEARS the search box and commits nothing (multiselectMixin.js:650-663), so
     * unlike the tag path there is no free text on the record - and there must not be. Dean, 20 Sep:
     * prompt, but commit nothing. Cancel therefore leaves the record exactly as it was, which is what
     * happens today when the text is discarded.
     */
    onModelListClosed: function () {
      const typed = this.lastModelSearch
      this.lastModelSearch = ''
      if (!typed) return
      //⚠ Nothing to ask about once they actually picked that value from the list.
      if (this.selectedModel && this.selectedModel.id > 0) return
      this.raiseTypedModelChoice(typed, false)
    },
    addToUniqueList: function (valueToAdd, listToAmmend) {
      if (!listToAmmend.includes(valueToAdd)) {
        listToAmmend.push(valueToAdd)
      }
    },
    /*
     * Puts back a trim or style the cascade cleared, and puts it back on its OWN OPTION LIST.
     *
     * ⚠ A helper where the colours make do with a bare `||`, because onSelectedModel empties
     * localTrims/localStyles as well as nulling the value. Restoring the value alone leaves it
     * selected but absent from its dropdown - open the list, pick anything, and it is gone with no
     * way back but retyping it as a tag. These are the same two writes setPropertiesFromSelectedMakeModel
     * makes when a record is opened for edit.
     *
     * ⚠ Guards on the COMPUTED list and pushes to the LOCAL array, which is that method's form and
     * not addToUniqueList's. addToUniqueList dedupes against the local array alone, so it would offer
     * a trim the catalogue model already carries twice.
     *
     * ⚠ Restores only where the cascade left the field empty, so a trim carfax actually supplied
     * still wins. This puts back what was destroyed; it does not override what was chosen.
     */
    restoreClearedOption: function (currentValue, previousValue, offeredOptions, localOptions) {
      if (currentValue || !previousValue) {
        return currentValue
      }
      if (!offeredOptions.includes(previousValue)) {
        localOptions.push(previousValue)
      }
      return previousValue
    },
    trimAdded: function (data) {
      data = this.titleCase(data)
      this.selectedTrim = data
      this.addToUniqueList(this.selectedTrim, this.localTrims)
      this.updateCustomMakeModel()
      this.fireOnChange()
      this.focusElement('style')
    },
    styleAdded: function (data) {
      data = this.titleCase(data)
      this.selectedStyle = data
      this.addToUniqueList(this.selectedStyle, this.localStyles)
      this.updateCustomMakeModel()
      this.fireOnChange()
      this.focusElement('extColor')
    },
    onExteriorColorAdded: function (data) {
      data = this.titleCase(data)
      this.selectedExteriorColor = data
      this.addToUniqueList(this.selectedExteriorColor, this.exteriorColors)
      this.exteriorColors.push(data)
      this.fireOnChange()
      this.focusElement('intColor')
    },
    onInteriorColorAdded: function (data) {
      data = this.titleCase(data)
      this.selectedInteriorColor = data
      this.addToUniqueList(this.selectedInteriorColor, this.interiorColors)
      this.fireOnChange()
      if (this.showAlternateColors) {
        this.focusElement('altExtColor')
      } else {
        this.focusElement('done')
      }
    },
    onAltExteriorColorAdded: function (data) {
      data = this.titleCase(data)
      this.addToUniqueList(data, this.selectedAltExteriorColor)
      this.addToUniqueList(data, this.altExteriorColors)
      this.fireOnChange()
      this.$nextTick(() => {
        this.focusElement('altExtColor')
      })
    },
    onAltInteriorColorAdded: function (data) {
      data = this.titleCase(data)
      this.addToUniqueList(data, this.selectedAltInteriorColor)
      this.addToUniqueList(data, this.altInteriorColors)
      this.fireOnChange()
      this.$nextTick(() => {
        this.focusElement('altIntColor')
      })
    },
    onTagAdded: function (data) {
      data = this.titleCase(data)
      this.addToUniqueList(data, this.selectedTags)
      this.addToUniqueList(data, this.availableTags)
      this.fireOnChange()
      this.$nextTick(() => {
        this.focusElement('tags')
      })
    },
    onSelectedLocateType: function (newValue) {
      if (newValue) {
        this.selectedLocateType = newValue
        if (this.selectedLocateType.name === 'New') {
          this.selectedSource = this.definedTypes.allowedSourceTypes.options.find((x) => x.id === this.definedTypes.allowedSourceTypes.Factory)
        } else {
          this.selectedSource = this.definedTypes.allowedSourceTypes.options.find((x) => x.id === this.definedTypes.allowedSourceTypes.Wholesaler)
        }
        this.fireOnChange()
        if (this.showCostSelector) {
          this.focusElement('purchaseCost')
        }
      }
    },
    onSelectedMake: function (newValue) {
      //⚠ A different make or year is a different catalogue, so answers about the old one no longer
      //apply. onSelectedYear already wipes localModels for the same reason.
      this.forgetAnsweredTypedModels()
      this.selectedYear = null
      this.selectedModel = null
      this.selectedTrim = null
      this.selectedStyle = null
      this.selectedExteriorColor = ''
      this.selectedInteriorColor = ''
      this.selectedMakeModel.makeId = newValue ? newValue.id : null
      this.selectedMakeModel.selectedMake = newValue
      this.selectedMakeModel.customMakeModel = ''
      this.localTrims = []
      this.localStyles = []
      //A different make means different models and different years, so anything carfax or the VIN
      //decode added for the previous one must not linger in the two lists.
      this.localModels = []
      this.localProductionYears = []
      this.updateCustomMakeModel()
      this.fireOnChange()
      this.focusElement('year')
    },
    /*
     * The year dropdown's own rendering, so an out-of-range year can be muted where it sits.
     *
     * ⚠ The raw option is NOT always a year. This field is :taggable, and when the typed value matches
     * nothing vue-multiselect injects { isTag: true, label: search } into the option list and hands that
     * object to this same slot (multiselectMixin.js :364/:366, Multiselect.vue :110). Rendering it
     * directly would print [object Object] on the "create this year" entry, so these two mirror the
     * library's own getOptionLabel: a tag renders its label, and is never treated as out of range.
     */
    yearOptionLabel: function (option) {
      return option && option.isTag ? option.label : option
    },
    isYearOutOfRange: function (option) {
      return !(option && option.isTag) && !isPlausibleProductionYear(option)
    },
    /*
     * The model dropdown's own rendering: a catalogue model is green, anything that will save as free
     * text is red, so the operator can see at a glance which rows are real.
     *
     * ⚠ Same :taggable trap as the year pair above. A tag option is { isTag: true, label: search } -
     * it has NO name and NO id, so it must be tested FIRST: `!(option.id > 0)` is true for it too, and
     * binding label="name" would render undefined. A tag is by definition not in the catalogue, so it
     * takes the same red as a free-text row.
     */
    modelOptionLabel: function (option) {
      if (!option) return ''
      return option.isTag ? option.label : option.name
    },
    modelOptionClass: function (option) {
      if (!option || option.isTag) return 'has-text-danger'
      return option.id > 0 ? 'has-text-success' : 'has-text-danger'
    },
    onSelectedYear: function (newValue) {
      //⚠ A different make or year is a different catalogue, so answers about the old one no longer
      //apply. onSelectedYear already wipes localModels for the same reason.
      this.forgetAnsweredTypedModels()
      this.selectedModel = null
      this.selectedTrim = null
      this.selectedStyle = null
      this.selectedMakeModel.productionYear = newValue
      this.localTrims = []
      this.localStyles = []
      //Models are scoped to the year; the year list itself is not, so localProductionYears stays.
      this.localModels = []
      this.updateCustomMakeModel()
      this.fireOnChange()
      this.focusElement('model')
    },
    onSelectedModel: function (newValue) {
      this.modelError = newValue && newValue.name.indexOf('_DELETED') > 0
      this.selectedTrim = null
      this.selectedStyle = null
      this.localTrims = []
      this.localStyles = []
      this.selectedMakeModel.modelId = newValue ? newValue.id : null
      this.selectedMakeModel.selectedModel = newValue
      /*
       * ⚠ A model the catalogue does not hold survives the save ONLY through customModelName:
       * EditInventory sends vehicleMakeVersionId as null for any id below 1, and writes the 'Model'
       * inventory attribute from this field. onSelectedModel set modelId and selectedModel but never
       * this, so a model reached through the pop-fill chip - the VIN-decode path - persisted as a
       * description with an EMPTY Model attribute, and the record lost the one name it had.
       */
      //⚠ $set, not a plain assignment. The component's own selectedMakeModel default carries no
      //customModelName key at all, and neither does the object VinNoSelector.resetDefaults builds -
      //so on the create path a plain write is not reactive, and unresolvedModelName (and with it the
      //model-group notice) never sees it.
      if (newValue && !(newValue.id > 0) && newValue.name) {
        this.$set(this.selectedMakeModel, 'customModelName', newValue.name)
      } else if (newValue && newValue.id > 0) {
        //⚠ Cleared when a real catalogue row is chosen, or a free-text name applied a moment earlier
        //survives the correction and persists as the record's Model attribute beside the catalogue
        //row that supersedes it.
        this.$set(this.selectedMakeModel, 'customModelName', '')
      }

      if (this.selectedMakeModel) {
        this.selectedTransmissionType = this.definedTypes.transmissionTypes.find((x) => x.altName === this.selectedMakeModel.selectedModel.defaultTransmissionType)
        // there must always be a make when selecting a model
        if (this.selectedMakeModel.makeId === -1) {
          this.selectedMakeModel.makeId = this.selectedMake ? this.selectedMake.id : null
          this.selectedMakeModel.selectedMake = this.selectedMake
        }
      }
      this.updateCustomMakeModel()
      this.fireOnChange()
      this.focusElement('trim')
    },
    onSelectedTrim: function (newValue) {
      this.selectedTrim = newValue
      this.updateCustomMakeModel()
      this.fireOnChange()
      this.focusElement('style')
    },
    onSelectedStyle: function (newValue) {
      this.selectedStyle = newValue
      this.updateCustomMakeModel()
      this.fireOnChange()
      this.focusElement('extColor')
    },
    onSelectedExteriorColor: function (newValue) {
      this.selectedExteriorColor = newValue
      this.updateCustomMakeModel()
      this.fireOnChange()
      this.focusElement('intColor')
    },
    onSelectedInteriorColor: function (newValue) {
      this.selectedInteriorColor = newValue
      this.updateCustomMakeModel()
      this.fireOnChange()
      if (this.showAlternateColors) {
        this.focusElement('altExtColor')
      } else {
        this.focusElement('done')
      }
    },
    onSelectedTransmissionType: function (newValue) {
      this.selectedTransmissionType = newValue
      this.fireOnChange()
      this.focusElement('done')
    },
    setPropertiesFromSelectedMakeModel: function () {
      //⚠ Remember what the record arrived on, BEFORE anything the operator does. This is what lets the
      //dropdown keep offering a model that has since been retired, and what lets onSave tell an
      //inherited deleted model from a newly chosen one.
      this.seededModelId = this.selectedMakeModel ? this.selectedMakeModel.modelId : null
      var foundMake = this.makes.find((x) => x.id === this.selectedMakeModel.makeId)
      var foundProductionYearModel = false
      if (foundMake) {
        console.log('Found make...')
        this.selectedMake = foundMake
        if (foundMake.versions && foundMake.versions.length > 0) {
          foundProductionYearModel = foundMake.versions.find((x) => x.id === this.selectedMakeModel.modelId)
          if (foundProductionYearModel) {
            console.log('Found production year...')
            this.selectedYear = foundProductionYearModel.productionYear
            this.selectedModel = foundProductionYearModel
          }
        }
      } else {
        console.log('Deriving custom make model...')
        if (this.selectedMakeModel.customMakeName) {
          this.selectedMake = {id: -1, name: this.selectedMakeModel.customMakeName, versions: [], definedColors: []}
        }
      }

      if (!foundProductionYearModel) {
        console.log('Deriving custom production year...')
        if (this.selectedMakeModel.productionYear) {
          let selectedYear = this.selectedMakeModel.productionYear
          /*
           * ⚠ The year is applied WHATEVER the catalogue holds. This assignment used to sit inside
           * the !includes guard below, so a decode whose year the catalogue already knew - the
           * ordinary case - left selectedYear null. The Model field is behind
           * v-show="selectedMake && selectedYear", so the operator got a make, no year, and NO MODEL
           * FIELD AT ALL until they picked the year by hand. Seen on ZFF83CLA4K0248000: the decode
           * returned productionYear 2019, 2019 is a Ferrari catalogue year, and the field stayed
           * hidden.
           */
          this.selectedYear = selectedYear
          if (!this.productionYears.includes(selectedYear)) {
            this.decodedProductionYear = selectedYear
            //⚠ localProductionYears, not the computed. `this.productionYears.push(...)` did not
            //survive the next invalidation, so a year the catalogue does not hold was selected but
            //never actually offered in the list.
            this.localProductionYears.push(selectedYear)
          }
        } else {
          this.selectedYear = null
        }

        if (this.selectedMakeModel.customModelName) {
          console.log('Deriving custom model...')
          let selectedModel = {id: -1, name: this.selectedMakeModel.customModelName, productionYear: this.selectedMakeModel.productionYear, trim: '', style: ''}
          if (!this.models.includes(selectedModel)) {
            this.selectedModel = selectedModel
            this.decodedModel = selectedModel
            //⚠ localModels, not the computed - same defect as the year above.
            this.localModels.push(selectedModel)
          }
        } else {
          this.selectedModel = null
        }
      }

      if (this.selectedMakeModel.trim && this.selectedMakeModel.trim.trim() !== '') {
        this.selectedTrim = this.selectedMakeModel.trim
        if (!this.trims.includes(this.selectedTrim)) {
          this.localTrims.push(this.selectedTrim)
        }
      } else {
        this.selectedTrim = null
      }

      if (this.selectedMakeModel.style && this.selectedMakeModel.style.trim() !== '') {
        this.selectedStyle = this.selectedMakeModel.style
        if (!this.styles.includes(this.selectedStyle)) {
          this.localStyles.push(this.selectedStyle)
        }
      } else {
        this.selectedStyle = null
      }
      
      this.originalSelectedInteriorColor = this.selectedMakeModel.interiorColor
      this.originalSelectedExteriorColor = this.selectedMakeModel.exteriorColor

      if (this.selectedMakeModel.interiorColor && this.selectedMakeModel.interiorColor.trim() !== '') {
        this.selectedInteriorColor = this.selectedMakeModel.interiorColor
        if (!this.interiorColors.includes(this.selectedInteriorColor)) {
          this.interiorColors.push(this.selectedInteriorColor)
        }
      } else {
        this.selectedInteriorColor = null
      }

      if (this.selectedMakeModel.exteriorColor && this.selectedMakeModel.exteriorColor.trim() !== '') {
        this.selectedExteriorColor = this.selectedMakeModel.exteriorColor
        if (!this.exteriorColors.includes(this.selectedExteriorColor)) {
          this.exteriorColors.push(this.selectedExteriorColor)
        }
      } else {
        this.selectedExteriorColor = null
      }

      if (this.selectedMakeModel.lookupResult && this.selectedMakeModel.lookupResult.valid === true) {
        let availableColors = this.selectedMakeModel.lookupResult.availableColors
        for (var availableColor of availableColors) {
          if (!this.exteriorColors.includes(availableColor)) {
            this.exteriorColors.push(availableColor)
          }
        }
        let availableTrims = this.selectedMakeModel.lookupResult.availableTrims
        for (var availableTrim of availableTrims) {
          if (!this.trims.includes(availableTrim)) {
            this.localTrims.push(availableTrim)
          }
        }
        let availableStyles = this.selectedMakeModel.lookupResult.availableStyles
        for (var availableStyle of availableStyles) {
          if (!this.styles.includes(availableStyle)) {
            this.localStyles.push(availableStyle)
          }
        }
      }

      this.modelError = this.selectedMakeModel && this.selectedMakeModel.selectedModel && this.selectedMakeModel.selectedModel.name.indexOf('_DELETED') > 0

      this.selectedLocateType = this.selectedMakeModel.locateType || this.selectedLocateType

      this.purchaseCost = this.selectedMakeModel.purchaseCost || this.purchaseCost

      this.selectedTitleStatus = this.selectedMakeModel.titleStatus || this.selectedTitleStatus

      this.warrantyExpirationDate = this.selectedMakeModel.warrantyExpirationDate || this.warrantyExpirationDate

      this.selectedBank = this.selectedMakeModel.selectedBank || this.selectedBank

      this.accNo = this.selectedMakeModel.accountNo || this.accNo

      this.payoffAmount = this.selectedMakeModel.payoffAmount || this.payoffAmount

      this.goodUntilDate = this.selectedMakeModel.goodUntilDate || this.goodUntilDate

      this.isDamaged = this.selectedMakeModel.isDamaged || this.isDamaged

      this.selectedVinNo = this.selectedMakeModel.vinNo || this.selectedVinNo

      this.selectedSource = this.selectedMakeModel.source || this.selectedSource

      if (this.selectedMakeModel.wholesaler && this.selectedMakeModel.wholesaler.id === null) this.selectedMakeModel.wholesaler = null
      this.selectedWholeSaler = this.selectedMakeModel.wholesaler || this.selectedWholeSaler

      this.selectedCustomer = this.selectedMakeModel.customer || this.selectedCustomer

      this.titleFiles = this.selectedMakeModel.titleFiles || this.packTitleFiles()

      this.damageFiles = this.selectedMakeModel.damageFiles || this.packDamageFiles()

      this.damageNote = this.selectedMakeModel.damageNote || this.damageNote

      this.usage = this.selectedMakeModel.usage || this.usage

      this.askingPrice = this.selectedMakeModel.askingPrice || this.askingPrice

      this.altExteriorColors = this.selectedMakeModel.altExteriorColors
      this.selectedAltExteriorColor = this.selectedMakeModel.altExteriorColors

      this.altInteriorColors = this.selectedMakeModel.altInteriorColors
      this.selectedAltInteriorColor = this.selectedMakeModel.altInteriorColors

      this.availableTags = this.selectedMakeModel.tags
      this.selectedTags = this.selectedMakeModel.tags

      this.notes = this.selectedMakeModel.notes
      this.selectedTransmissionType = this.definedTypes.transmissionTypes.find((x) => x.altName === this.selectedMakeModel.transmissionType)

      this.selectedMakeModel.locateType = this.selectedLocateType
      this.selectedMakeModel.titleStatus = this.selectedTitleStatus
      this.selectedMakeModel.warrantyExpirationDate = this.warrantyExpirationDate
      this.selectedMakeModel.selectedBank = this.selectedBank
      this.selectedMakeModel.accountNo = this.accNo
      this.selectedMakeModel.payoffAmount = this.payoffAmount
      this.selectedMakeModel.goodUntilDate = this.goodUntilDate
      this.selectedMakeModel.isDamaged = this.isDamaged
      this.selectedMakeModel.titleFiles = this.packTitleFiles()
      this.selectedMakeModel.damageFiles = this.packDamageFiles()
      this.selectedMakeModel.damageNote = this.damageNote
      this.selectedMakeModel.vinNo = this.selectedVinNo
      this.selectedMakeModel.source = this.selectedSource
      this.selectedMakeModel.wholesaler = this.selectedWholeSaler
      this.selectedMakeModel.customer = this.selectedCustomer
      this.selectedMakeModel.purchaseCost = this.purchaseCost
      this.selectedMakeModel.usage = this.usage
      this.selectedMakeModel.askingPrice = this.askingPrice
    },
    setCpoStatus: function () {
      this.selectedMakeModel.cpoAllowed = true
      this.selectedMakeModel.autoCpoStatus = 'Ineligible'
      this.selectedMakeModel.vehicleAge = null
      this.selectedMakeModel.isDealerMake = this.isDealerMake()
      this.selectedMakeModel.defaultMakeWarrantyPeriod = this.selectedMake && this.selectedMake.defaultWarrantyPeriod ? this.selectedMake.defaultWarrantyPeriod : null
      this.selectedMakeModel.defaultModelWarrantyPeriod = this.selectedModel && this.selectedModel.defaultWarrantyPeriod ? this.selectedModel.defaultWarrantyPeriod : null
      if (this.selectedMakeModel && this.isDealerMake()) {
        var age = this.vehicleAge
        this.selectedMakeModel.vehicleAge = age
        if (age >= 0 && age <= this.getCurrentDealer.autoCpoAgeLimit) {
          this.selectedMakeModel.cpoAllowed = true
          this.selectedMakeModel.autoCpoStatus = 'Cpo'
        } else if (age > this.getCurrentDealer.autoCpoAgeLimit && age <= this.getCurrentDealer.cpoQualifyingAge) {
          this.selectedMakeModel.cpoAllowed = true
          this.selectedMakeModel.autoCpoStatus = 'Eligible'
        }
      }
    },
    fireOnChange: function () {
      this.selectedMakeModel.locateType = this.selectedLocateType
      this.selectedMakeModel.titleStatus = this.selectedTitleStatus
      this.selectedMakeModel.warrantyExpirationDate = this.warrantyExpirationDate
      this.selectedMakeModel.selectedBank = this.selectedBank
      this.selectedMakeModel.accountNo = this.accNo
      this.selectedMakeModel.payoffAmount = this.payoffAmount
      this.selectedMakeModel.goodUntilDate = this.goodUntilDate
      this.selectedMakeModel.isDamaged = this.isDamaged
      this.selectedMakeModel.titleFiles = this.packTitleFiles()
      this.selectedMakeModel.damageFiles = this.packDamageFiles()
      this.selectedMakeModel.damageNote = this.damageNote
      this.selectedMakeModel.exteriorColor = this.selectedExteriorColor
      this.selectedMakeModel.interiorColor = this.selectedInteriorColor
      this.selectedMakeModel.altExteriorColors = this.selectedAltExteriorColor
      this.selectedMakeModel.altInteriorColors = this.selectedAltInteriorColor
      this.selectedMakeModel.trim = this.selectedTrim
      this.selectedMakeModel.style = this.selectedStyle
      this.selectedMakeModel.tags = this.selectedTags
      this.selectedMakeModel.overview = this.overview
      this.selectedMakeModel.completeOverview = this.overview + ' ' + this.smallColorOverview
      this.selectedMakeModel.isLocated = this.isLocated
      this.selectedMakeModel.transmissionType = this.selectedTransmissionType ? this.selectedTransmissionType.altName : null

      this.setCpoStatus()
      this.$emit('input', this.selectedMakeModel)
      this.$markDirty()
    },
    onPreviewMakeModelListener: function () {
      this.isConfiguring = true
      /// eventBus.$emit('hide-located-modal')
      //⚠ A fresh decode reached the record, so any previous answer is void - and the lookup to read is
      //the record's, not whatever this component decoded for its own pop-fills earlier.
      this.beginDecodedModelChoice(null)
      this.offerModelListWhenNotInCatalogue()
    },
    initFromValue: function (newValue) {
      this.selectedMakeModel = newValue
      this.isModelInfoCollapsed = this.collapseMakeModelInfo
      this.setPropertiesFromSelectedMakeModel()
    },
    onSubmitForm () {
      this.$refs.done.click()
    },
    /*
     * ⚠ These set ONLY their own flag. They used to also assign this.useModal, which is a prop - see
     * isReportOpen, which the layout branches now derive from these two flags instead.
     */
    onShowAutoCheckReport: function () {
      this.showReport = true
    },
    onHideAutoCheckReport: function () {
      this.showReport = false
    },
    onShowCarFaxReport: function () {
      this.showCarFaxReport = true
    },
    onHideCarFaxReport: function () {
      this.showCarFaxReport = false
    },
    uploadStarted: function () {
      this.fileUploadCount++
      this.isUploadingFile = true
      eventBus.$emit('file-uploads-started')
    },
    uploadComplete: function () {
      this.fileUploadCount--
      if (this.fileUploadCount === 0) {
        this.isUploadingFile = false
        eventBus.$emit('file-uploads-completed')
      }
    },
    /*
     * Applies what CARFAX returned straight into the form, instead of offering it as chips the
     * operator then has to click one at a time.
     *
     * ⚠ This is Garrett's change 3, and his complaint was not that it took too many clicks - it was
     * that there was nowhere to accept the suggestion at all: "nothing here gives them a place to
     * actually select that selection. So they're leaving it like this. That car does not exist as a
     * 296." It fills the FORM and stops there; Done still saves, so nothing is written behind the
     * operator's back.
     *
     * ⚠ ORDER IS LOAD-BEARING - make, year, model, trim, style. onSelectedMake nulls the year, the
     * model, the trim and the style; onSelectedYear nulls the model, trim and style; onSelectedModel
     * nulls the trim and style and empties localTrims/localStyles. Applying in any other order
     * silently wipes what was just applied, and the failure looks like carfax having returned less
     * than it did.
     *
     * ⚠ The first three set the ref AND call the handler; the last two only call the handler. That
     * asymmetry is real: make/year/model are bound with :value + @on-change so the multiselect's own
     * v-model would normally do the assignment, while onSelectedTrim/onSelectedStyle assign
     * internally. Setting trim or style here as well would be harmless but misleading.
     *
     * ⚠ Applies as FREE TEXT when carfax's model resolves to no catalogue row, rather than falling
     * back to suggest-only. That is the common failure and not an edge case - 14 of the 60 active
     * records carrying a carfax model resolve to nothing, and 99 active makes hold no catalogue
     * models at all - and the suggest-only branch applied exactly one thing, selectedMake, while
     * dropping the model entirely: decodedModel is assigned only on the resolved path, so there was
     * not even a chip to click. Garrett, 1 Sep: "When I click yes, it just shows me G80. Nothing came
     * over from the Carfax... they end up with the bad data that they started with."
     *
     * ⚠ Accuracy over the catalogue link, deliberately (Dean, 9 Sep). Two of those records currently
     * hold a catalogue row that carfax disagrees with, and applying free text drops that link. A
     * record filed under the right name with no link still reports better than one filed under the
     * wrong model.
     *
     * ⚠ No candidates guard. Applying a group name like "812" as free text is exactly what makes
     * modelGroupCandidates fire, narrow the list and drop the free-text entry - so the record
     * self-corrects. Guarding it instead would leave a STALE modelId on the bound object, because
     * onSelectedYear nulls selectedModel without touching selectedMakeModel.
     */
    /*
     * ⚠ chosenModel is the row the operator ticked in the reconcile table, and it WINS over carfax's
     * own model when it is a catalogue row. That is the whole point of the table: carfax's model is
     * often a group name the catalogue does not hold ("812" against "812 Superfast"), so applying it
     * verbatim files the vehicle as free text and drops it out of model reporting. Everything else -
     * make, year, trim, style - still comes from carfax.
     */
    applyCarfaxMakeModel: function (carfaxMakeModel, chosenModel) {
      /*
       * ⚠ FIRST, and before the make guards, because keeping the current model means changing
       * nothing at all - including the free-text fallback below, which a record with no matching
       * make would otherwise reach. Meeting change #15: this row used to arrive as null, which is
       * also what "nothing ticked" arrives as, so Apply on "Currently selected" applied CARFAX.
       */
      if (isKeepCurrentModel(chosenModel)) return

      //⚠ Returns rather than delegating when there is no make: setMakeModelFromCarfaxData opens with
      //an unguarded carfaxMakeModel.selectedMake.id. In practice a rating with no matching make never
      //raises the prompt at all, so neither method is reached - but this method is now the entry
      //point and must not be the one that turns that into a crash.
      if (!carfaxMakeModel || !carfaxMakeModel.selectedMake) return

      const foundMake = this.makes.find((x) => x.id === carfaxMakeModel.selectedMake.id)
      if (!foundMake) {
        this.setMakeModelFromCarfaxData(carfaxMakeModel)
        return
      }

      /*
       * ⚠ The operator's choice first. A ticked catalogue row is applied as the model, and the rest of
       * the carfax values still cascade from carfaxMakeModel - so choosing "812 Superfast" against a
       * carfax model of "812" gives the record a real catalogue link instead of free text.
       */
      if (chosenModel && chosenModel.id > 0) {
        this.applyMakeModelCascade(foundMake, chosenModel.productionYear, chosenModel, carfaxMakeModel)
        return
      }

      const foundModel = carfaxMakeModel.selectedModel && foundMake.versions
        ? foundMake.versions.find((x) => x.id === carfaxMakeModel.modelId)
        : null

      if (foundModel) {
        this.applyMakeModelCascade(foundMake, foundModel.productionYear, foundModel, carfaxMakeModel)
        return
      }

      //⚠ Read from EITHER shape. The second suggest-only return was reached with selectedModel set
      //and customModelName empty - the catalogue simply held no row with that id - and onSelectedModel
      //does newValue.name.indexOf('_DELETED'), so a name resolved only from customModelName would
      //throw a TypeError there. With neither, there is genuinely nothing to apply.
      const modelName = carfaxMakeModel.customModelName ||
        (carfaxMakeModel.selectedModel && carfaxMakeModel.selectedModel.name)
      if (!isMeaningfulValue(modelName)) {
        this.setMakeModelFromCarfaxData(carfaxMakeModel)
        return
      }

      //⚠ The year is captured BEFORE the cascade, and no year at all means suggest-only. Without
      //that, a rating with no year left selectedYear nulled by onSelectedMake - and the Model field
      //is v-show="selectedMake && selectedYear" while canCancelDialog defaults to false, so the
      //operator was trapped in a modal they could neither dismiss nor submit.
      const year = this.usableCarfaxYear(carfaxMakeModel.productionYear)
      if (!year) {
        this.setMakeModelFromCarfaxData(carfaxMakeModel)
        return
      }

      //Same synthetic shape the VIN decode already offers for a model the catalogue does not hold.
      const freeTextModel = { id: -1, name: String(modelName).trim(), productionYear: year, trim: '', style: '' }
      this.applyMakeModelCascade(foundMake, year, freeTextModel, carfaxMakeModel)
    },
    /*
     * The carfax year, or the record's own when carfax has none or an unusable one.
     *
     * ⚠ The carfax year is not validated anywhere on the way in - it comes straight off the PDF
     * extraction - and that is exactly how the two live "296 GTB" catalogue rows at 2034 and 2037
     * were created. Refused and said so, in the same words onFillProductionYear uses, rather than
     * applied or silently dropped.
     */
    usableCarfaxYear: function (carfaxYear) {
      if (carfaxYear) {
        if (isPlausibleProductionYear(carfaxYear)) {
          this.yearError = ''
          return carfaxYear
        }
        this.yearError = `Carfax reported ${carfaxYear}, which is not a usable production year.`
      }
      return this.selectedYear
    },
    /*
     * The five-field cascade both apply paths share - make, year, model, trim, style.
     *
     * ⚠ The make and year handlers are SKIPPED when the record already holds those values. The prompt
     * commonly fires on the model, trim or style alone (vehicleMakeDifferent false), so most accepts
     * need no cascade at all - and not running it is what keeps the colour wipe below out of reach in
     * the ordinary case. The bound object is still brought into step by hand where they are skipped.
     */
    applyMakeModelCascade: function (foundMake, year, model, carfaxMakeModel) {
      this.isConfiguring = true

      //⚠ Snapshotted BEFORE the cascade, because the cascade destroys them. onSelectedMake blanks
      //both colours and onSelectedModel derives the transmission from the chosen model's own default
      //- a free-text model has none - so fireOnChange writes '' and null onto the bound object, and
      //setInventoryAttribute marks a stored row Deleted when handed a blank. Every accepted CARFAX
      //apply was therefore DELETING the vehicle's exterior and interior colour. EditInventory's
      //forceColorSelection makes the operator re-pick, which masks it; ApprovePpaAction passes
      //forceColorSelection="false" and still persists them, so on the PPA screen it was silent.
      //⚠ The alternate colours are deliberately not snapshotted - checked, nothing in the cascade
      //touches them.
      const previousExteriorColor = this.selectedExteriorColor
      const previousInteriorColor = this.selectedInteriorColor
      const previousTransmissionType = this.selectedTransmissionType
      /*
       * ⚠ Trim and style are snapshotted for the same reason and by a worse route. onSelectedModel
       * nulls both outright. The write below is guarded against a blank carfax trim, but that guard
       * runs after onSelectedModel has already nulled the stored one.
       *
       * ⚠ Still snapshotted although the model is now skipped when it already matches. The skip
       * covers the no-change apply; a genuine model change still cascades and still nulls both.
       */
      const previousTrim = this.selectedTrim
      const previousStyle = this.selectedStyle

      const makeAlreadySelected = !!this.selectedMake && this.selectedMake.id === foundMake.id
      this.selectedMake = foundMake
      if (makeAlreadySelected) {
        this.selectedMakeModel.makeId = foundMake.id
        this.selectedMakeModel.selectedMake = foundMake
      } else {
        this.onSelectedMake(foundMake)
      }

      this.addToUniqueList(year, this.localProductionYears)
      if (this.selectedYear === year) {
        this.selectedMakeModel.productionYear = year
      } else {
        this.selectedYear = year
        this.onSelectedYear(year)
      }

      this.addToUniqueList(model, this.localModels)
      /*
       * ⚠ Skipped when the record already holds this model, the same way the make and the year above
       * are. onSelectedModel nulls selectedTrim and selectedStyle outright, so without this an apply
       * that changes nothing still destroyed both and relied on the restore below to put them back -
       * and the restore only fires where the cascade left the field EMPTY, so anything it could not
       * match was simply lost.
       *
       * ⚠ CATALOGUE ROWS ONLY. Every free-text model carries id -1, so comparing ids alone would read
       * two DIFFERENT free-text models as the same one and silently keep the old name.
       */
      const modelAlreadySelected = !!model && model.id > 0 &&
        !!this.selectedModel && this.selectedModel.id === model.id
      this.selectedModel = model
      if (modelAlreadySelected) {
        this.selectedMakeModel.modelId = model.id
        this.selectedMakeModel.selectedModel = model
      } else {
        this.onSelectedModel(model)
      }

      //⚠ Written only when carfax actually has a value, and 'N/A' is not one - CARFAX sends that
      //literal for a field it has no answer for, and STYLE is guarded for it nowhere upstream. A
      //blank would DELETE a trim or style the operator had set, and a resolved model whose name
      //already carries the sub-model legitimately has no trim left.
      if (isMeaningfulValue(carfaxMakeModel.trim)) {
        this.addToUniqueList(carfaxMakeModel.trim, this.localTrims)
        this.onSelectedTrim(carfaxMakeModel.trim)
      }

      if (isMeaningfulValue(carfaxMakeModel.style)) {
        this.addToUniqueList(carfaxMakeModel.style, this.localStyles)
        this.onSelectedStyle(carfaxMakeModel.style)
      }

      //⚠ Restored only where the cascade left the field EMPTY, so a model that genuinely carries a
      //default transmission still wins. This puts back what was destroyed; it does not override what
      //was chosen.
      this.selectedExteriorColor = this.selectedExteriorColor || previousExteriorColor
      this.selectedInteriorColor = this.selectedInteriorColor || previousInteriorColor
      this.selectedTransmissionType = this.selectedTransmissionType || previousTransmissionType
      this.selectedTrim = this.restoreClearedOption(this.selectedTrim, previousTrim, this.trims, this.localTrims)
      this.selectedStyle = this.restoreClearedOption(this.selectedStyle, previousStyle, this.styles, this.localStyles)
      this.fireOnChange()
    },
    setMakeModelFromCarfaxData: function (carfaxMakeModel) {
      this.isConfiguring = true
      //⚠ Reached from applyCarfaxMakeModel's three suggest-only fallbacks, so every chip it arms is a
      //CARFAX value. It never said so, and a fresh selector still read 'vindecode'.
      this.offeredValueSource = 'the CARFAX report'
      var foundMake = this.makes.find((x) => x.id === carfaxMakeModel.selectedMake.id)
      var foundProductionYearModel = false
      if (foundMake) {
        this.decodedMake = foundMake
        if (!this.selectedMake) {
          this.selectedMake = foundMake
        }
        if (foundMake.versions && foundMake.versions.length > 0) {
          foundProductionYearModel = foundMake.versions.find((x) => x.id === carfaxMakeModel.modelId)
          if (foundProductionYearModel) {
            this.decodedProductionYear = foundProductionYearModel.productionYear
            this.decodedModel = foundProductionYearModel
          }
        }
      }

      if (!foundProductionYearModel) {
        if (carfaxMakeModel.productionYear) {
          let selectedYear = carfaxMakeModel.productionYear
          if (!this.productionYears.includes(selectedYear)) {
            this.decodedProductionYear = selectedYear
            //⚠ localProductionYears, not the computed. `this.productionYears.push(...)` did not
            //survive the next invalidation, so the offered year was never actually in the list.
            this.localProductionYears.push(selectedYear)
          }
        }
      }

      if (carfaxMakeModel.trim && carfaxMakeModel.trim.trim() !== '') {
        let trim = carfaxMakeModel.trim
        this.decodedTrim = trim
        if (!this.trims.includes(trim)) {
          this.localTrims.push(trim)
        }
      }

      if (carfaxMakeModel.style && carfaxMakeModel.style.trim() !== '') {
        let style = carfaxMakeModel.style
        this.decodedStyle = style
        if (!this.styles.includes(style)) {
          this.localStyles.push(style)
        }
      }
    },
    onFillTrim: function () {
      let trim = this.decodedTrim
      if (!this.trims.includes(trim)) {
        this.localTrims.push(trim)
      }
      this.selectedTrim = this.decodedTrim
      this.updateCustomMakeModel()
      this.fireOnChange()
    },
    onFillStyle: function () {
      let style = this.decodedStyle
      if (!this.styles.includes(style)) {
        //⚠ localStyles, not localTrims. This pushed the style into the TRIM list, so a decoded style
        //the catalogue does not hold was offered as a trim and never as a style.
        this.localStyles.push(style)
      }      
      this.selectedStyle = style
      this.updateCustomMakeModel()
      this.fireOnChange()
    }
  },
  watch: {
    /*
     * ⚠ Fetched EVERY time the comparison opens, never cached: a cached answer would offer the
     * pre-correction suggestion straight after a correction is saved (code review on #1589).
     */
    carfaxLearningRequestKey: {
      immediate: true,
      handler: function () {
        this.fetchCarfaxLearning()
      }
    },
    value: function (newValue, oldValue) {
      if (!newValue) {
        return
      }
      this.initFromValue(newValue)
    },
    /*
     * ⚠ The host needs this to know which presentation of the CARFAX comparison to use, and $refs
     * are not reactive - a v-if in the host's template cannot read this component's data directly.
     */
    isConfiguring: function (newValue, oldValue) {
      this.$emit('configuring-change', newValue === true)
      /*
       * ⚠ The comparison-only flag dies with the selector, whichever route closed it - Done, Start
       * Over, or the host. Left set, the NEXT open would inherit it and a Cancel on a comparison the
       * operator raised themselves would close the form they were working in.
       */
      if (newValue !== true) {
        this.openedForCarfaxComparisonOnly = false
      }
    },
    purchaseCost: function (newValue, oldValue) {
      this.selectedMakeModel.purchaseCost = newValue
      this.fireOnChange()
    },
    usage: function (newValue, oldValue) {
      this.selectedMakeModel.usage = newValue
      this.fireOnChange()
    },
    askingPrice: function (newValue, oldValue) {
      this.selectedMakeModel.askingPrice = newValue
      this.fireOnChange()
    },
    selectedVinNo: function (newValue, oldValue) {
      this.selectedMakeModel.vinNo = newValue
      this.fireOnChange()
    },
    selectedTitleStatus: function (newValue, oldValue) {
      this.selectedMakeModel.titleStatus = newValue
      this.fireOnChange()
    },
    warrantyExpirationDate: function (newValue, oldValue) {
      this.selectedMakeModel.warrantyExpirationDate = newValue
      this.fireOnChange()
    },
    selectedBank: function (newValue, oldValue) {
      this.selectedMakeModel.selectedBank = newValue
      this.fireOnChange()
    },
    accNo: function (newValue, oldValue) {
      this.selectedMakeModel.accountNo = newValue
      this.fireOnChange()
    },
    damageNote: function (newValue, oldValue) {
      this.selectedMakeModel.damageNote = newValue
      this.fireOnChange()
    },
    payoffAmount: function (newValue, oldValue) {
      this.selectedMakeModel.payoffAmount = newValue
      this.fireOnChange()
    },
    isDamaged: function (newValue, oldValue) {
      this.selectedMakeModel.isDamaged = newValue
      this.fireOnChange()
    },
    goodUntilDate: function (newValue, oldValue) {
      this.selectedMakeModel.goodUntilDate = newValue
      this.fireOnChange()
    },
    selectedSource: function (newValue, oldValue) {
      this.selectedMakeModel.source = newValue
      this.fireOnChange()
    },
    selectedWholeSaler: function (newValue, oldValue) {
      this.selectedMakeModel.wholesaler = newValue
      this.fireOnChange()
    },
    selectedCustomer: function (newValue, oldValue) {
      this.selectedMakeModel.customer = newValue
      this.fireOnChange()
    },
    selectedTags: function (newValue, oldValue) {
      this.fireOnChange()
      this.$nextTick(() => {
        this.focusElement('tags')
      })
    },
    selectedAltExteriorColor: function (newValue, oldValue) {
      this.fireOnChange()
      this.$nextTick(() => {
        this.focusElement('altExtColor')
      })
    },
    selectedAltInteriorColor: function (newValue, oldValue) {
      this.fireOnChange()
      this.$nextTick(() => {
        this.focusElement('altIntColor')
      })
    }
    //⚠ The selectedMake watcher that used to live here is GONE, along with the `versions` data field
    //it maintained. It did `this.versions = this.$lodash.reverse(newValue.versions)` - and lodash
    //reverse mutates IN PLACE, so it reversed the Vuex catalogue array itself. That flipped the order
    //for every other screen sharing the store, and flip-flopped it on each re-selection of the same
    //make, which is why the "highest to lowest" complaint was never reproducible on demand. Its only
    //reader was productionYears, which now sorts properly on its own.
  },
  created: function () {
    if (this.useModal) {

    } else {

    }

    if (this.useValidator) {
      this.$validator = this.useValidator
    } else {
      this.$validator = this.$parent.$validator
    }

    eventBus.$on('preview-make-model', this.onPreviewMakeModelListener)
    eventBus.$on('submit-make-model-form', this.onSubmitForm)
  },
  destroyed: function () {
    eventBus.$off('preview-make-model', this.onPreviewMakeModelListener)
    eventBus.$off('submit-make-model-form', this.onSubmitForm)
  },
  mounted () {
    if (this.value)  {
      this.initFromValue(this.value)
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
/* Holds the place of the CARFAX options while their learned mappings load; b-loading covers it. */
.carfax-learning-loading {
  position: relative;
  min-height: 8rem;
}


  html body .modal.is-active .modal-card-body {
    padding: 20px 10px;
  }

  .status-icon {
    margin-left:15px;
  }

  .selected-make-model {
    width: 250px;
    white-space: normal;
    min-height: 3em;
    height: auto;
  }

  .column {
    padding: 0.5rem;
  }

  .modal-card-body {
    position:relative;
    max-height: calc(100vh - 220px);
    overflow-y: auto;
    background: white;
  }

  .model-not-in-catalogue {
    color: red;
    font-weight: bold;
    font-size: 12px;
    margin-top: 6px;
  }

  /*
   * ⚠ ::v-deep, because the option rows belong to vue-multiselect, not this template, so a plain
   * scoped rule never reaches them. The highlighted row is dark with white text
   * (css/vue-multiselect.css) which would otherwise wash the green and red straight out on hover.
   */
  ::v-deep .multiselect__option--highlight .has-text-success,
  ::v-deep .multiselect__option--highlight .has-text-danger {
    color: #fff !important;
    font-weight: bold;
  }

  .model-error-container {
    color:red;
    font-weight: bold;
    font-size: 12px;
    margin-bottom: 10px;
  }
</style>
